<template>
  <div class="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 transition-colors duration-300">
    <!-- Navigation -->
    <nav class="bg-white dark:bg-gray-800 shadow-lg sticky top-0 z-50 border-b border-gray-200 dark:border-gray-700 transition-colors duration-300">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex justify-between items-center h-16">
          <div class="flex-shrink-0 flex items-center space-x-3">
            <h1 class="text-xl font-semibold text-gray-900 dark:text-white">
              Admin Panel
            </h1>
          </div>
          <div class="flex items-center space-x-4">
            <router-link 
              to="/" 
              class="text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 px-3 py-2 rounded-md text-sm font-medium transition-colors"
            >
              Zur Startseite
            </router-link>
            <button
              @click="handleLogout"
              class="bg-red-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-red-700 dark:hover:bg-red-500 transition-colors shadow-sm"
            >
              Abmelden
            </button>
          </div>
        </div>
      </div>
    </nav>

    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <!-- Haupttabs -->
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow-md mb-6 border border-gray-200 dark:border-gray-700 transition-colors duration-300">
        <div class="border-b border-gray-200 dark:border-gray-700">
          <nav class="flex space-x-8 px-6" aria-label="Haupttabs">
            <router-link
              v-for="tab in mainTabs"
              :key="tab.id"
              :to="tab.path"
              :class="[
                'py-4 px-1 border-b-2 font-medium text-sm transition-all duration-200',
                activeTab === tab.id
                  ? 'border-blue-500 dark:border-blue-400 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              ]"
            >
              {{ tab.name }}
            </router-link>
          </nav>
        </div>
      </div>

      <!-- Sekundäre Tab-Leiste für Einstellungen -->
      <div v-if="activeTab === 'settings'" class="bg-white dark:bg-gray-800 rounded-lg shadow-md mb-6 border border-gray-200 dark:border-gray-700 transition-colors duration-300">
        <div class="border-b border-gray-200 dark:border-gray-700">
          <nav class="flex space-x-8 px-6" aria-label="Einstellungs-Untertabs">
            <router-link
              v-for="subTab in settingsSubTabs"
              :key="subTab.id"
              :to="subTab.path"
              :class="[
                'py-4 px-1 border-b-2 font-medium text-sm transition-all duration-200',
                activeSubTab === subTab.id
                  ? 'border-blue-500 dark:border-blue-400 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              ]"
            >
              {{ subTab.name }}
            </router-link>
          </nav>
        </div>
      </div>

      <!-- Sekundäre Tab-Leiste für Kontaktformulare -->
      <div v-if="activeTab === 'contact-form'" class="bg-white dark:bg-gray-800 rounded-lg shadow-md mb-6 border border-gray-200 dark:border-gray-700 transition-colors duration-300">
        <div class="border-b border-gray-200 dark:border-gray-700">
          <nav class="flex space-x-8 px-6" aria-label="Kontaktformular-Untertabs">
            <router-link
              v-for="subTab in contactFormSubTabs"
              :key="subTab.id"
              :to="subTab.path"
              :class="[
                'py-4 px-1 border-b-2 font-medium text-sm transition-all duration-200',
                activeContactFormSubTab === subTab.id
                  ? 'border-blue-500 dark:border-blue-400 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              ]"
            >
              {{ subTab.name }}
            </router-link>
          </nav>
        </div>
      </div>

      <!-- Tab Content -->
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 border border-gray-200 dark:border-gray-700 transition-colors duration-300">

        <!-- Einstellungen Tab - Bilder -->
        <div v-if="activeTab === 'settings' && activeSubTab === 'images'">
          <div class="mb-6">
            <h2 class="text-2xl font-bold text-gray-900 dark:text-white mb-2">Bildverwaltung</h2>
            <p class="text-gray-600 dark:text-gray-400">Laden Sie Bilder für die Home- und Über-mich-Seite hoch</p>
          </div>

          <!-- Upload Bereich -->
          <div class="grid md:grid-cols-3 gap-6 mb-8">
            <!-- Home Bilder -->
            <div class="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 bg-gray-50 dark:bg-gray-900/50 transition-colors duration-300">
              <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Home-Seite</h3>
              <div class="space-y-4">
                <div class="relative">
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Bildname <span class="text-red-500">*</span>
                    <span class="text-xs text-gray-500 dark:text-gray-400 font-normal">(z.B. "profile", "hero-image")</span>
                  </label>
                  <div class="relative">
                    <input
                      v-model="homeImageName"
                      type="text"
                      @input="validateHomeImageName"
                      @focus="showHomeSuggestions = true"
                      @blur="hideHomeSuggestions"
                      :class="[
                        'w-full px-4 py-2 border rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-colors duration-200',
                        homeImageNameError ? 'border-red-500 dark:border-red-500' : 'border-gray-300 dark:border-gray-600'
                      ]"
                      placeholder="profile"
                      autocomplete="off"
                    />
                    <!-- Dropdown Vorschläge -->
                    <div
                      v-if="showHomeSuggestions && homeImageNameSuggestions.length > 0"
                      class="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-48 overflow-auto"
                    >
                      <button
                        v-for="suggestion in homeImageNameSuggestions"
                        :key="suggestion"
                        type="button"
                        @click="selectHomeImageName(suggestion)"
                        class="w-full text-left px-4 py-2 text-sm text-gray-900 dark:text-gray-100 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                      >
                        {{ suggestion }}
                      </button>
                    </div>
                  </div>
                  <p v-if="homeImageNameError" class="mt-1 text-sm text-red-600 dark:text-red-400">
                    {{ homeImageNameError }}
                  </p>
                  <p v-else-if="homeImageName && validateImageName(homeImageName).valid" class="mt-1 text-xs text-green-600 dark:text-green-400">
                    ✓ Gültiger Bildname
                  </p>
                </div>
                <div>
                  <input
                    ref="homeImageInput"
                    type="file"
                    accept="image/*"
                    @change="(e) => handleImageUpload(e, 'home')"
                    class="hidden"
                  />
                  <button
                    type="button"
                    @click="() => { if (homeImageInput) homeImageInput.click() }"
                    :disabled="uploadingHome"
                    class="w-full border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-md px-4 py-8 text-center hover:border-blue-500 dark:hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg class="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    <p class="mt-2 text-sm text-gray-600 dark:text-gray-400">
                      <span v-if="uploadingHome">Wird hochgeladen...</span>
                      <span v-else>Bild für Home-Seite hochladen</span>
                    </p>
                  </button>
                </div>
              </div>
            </div>

            <!-- Über-mich Bilder -->
            <div class="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 bg-gray-50 dark:bg-gray-900/50 transition-colors duration-300">
              <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Über-mich-Seite</h3>
              <div class="space-y-4">
                <div class="relative">
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Bildname <span class="text-red-500">*</span>
                    <span class="text-xs text-gray-500 dark:text-gray-400 font-normal">(z.B. "profile", "about-hero")</span>
                  </label>
                  <div class="relative">
                    <input
                      v-model="aboutImageName"
                      type="text"
                      @input="validateAboutImageName"
                      @focus="showAboutSuggestions = true"
                      @blur="hideAboutSuggestions"
                      :class="[
                        'w-full px-4 py-2 border rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-colors duration-200',
                        aboutImageNameError ? 'border-red-500 dark:border-red-500' : 'border-gray-300 dark:border-gray-600'
                      ]"
                      placeholder="profile"
                      autocomplete="off"
                    />
                    <!-- Dropdown Vorschläge -->
                    <div
                      v-if="showAboutSuggestions && aboutImageNameSuggestions.length > 0"
                      class="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-48 overflow-auto"
                    >
                      <button
                        v-for="suggestion in aboutImageNameSuggestions"
                        :key="suggestion"
                        type="button"
                        @click="selectAboutImageName(suggestion)"
                        class="w-full text-left px-4 py-2 text-sm text-gray-900 dark:text-gray-100 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                      >
                        {{ suggestion }}
                      </button>
                    </div>
                  </div>
                  <p v-if="aboutImageNameError" class="mt-1 text-sm text-red-600 dark:text-red-400">
                    {{ aboutImageNameError }}
                  </p>
                  <p v-else-if="aboutImageName && validateImageName(aboutImageName).valid" class="mt-1 text-xs text-green-600 dark:text-green-400">
                    ✓ Gültiger Bildname
                  </p>
                </div>
                <div>
                  <input
                    ref="aboutImageInput"
                    type="file"
                    accept="image/*"
                    @change="(e) => handleImageUpload(e, 'about')"
                    class="hidden"
                  />
                  <button
                    type="button"
                    @click="() => { if (aboutImageInput) aboutImageInput.click() }"
                    :disabled="uploadingAbout"
                    class="w-full border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-md px-4 py-8 text-center hover:border-blue-500 dark:hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg class="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    <p class="mt-2 text-sm text-gray-600 dark:text-gray-400">
                      <span v-if="uploadingAbout">Wird hochgeladen...</span>
                      <span v-else>Bild für Über-mich-Seite hochladen</span>
                    </p>
                  </button>
                </div>
              </div>
            </div>

            <!-- Hero-Website Bilder -->
            <div class="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 bg-gray-50 dark:bg-gray-900/50 transition-colors duration-300">
              <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Hero-Websites</h3>
              <div class="space-y-4">
                <div class="relative">
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Bildname <span class="text-red-500">*</span>
                    <span class="text-xs text-gray-500 dark:text-gray-400 font-normal">(z.B. "m-drei", "verkaufszaehler")</span>
                  </label>
                  <div class="relative">
                    <input
                      v-model="heroWebsiteImageName"
                      type="text"
                      @input="validateHeroWebsiteImageName"
                      @focus="showHeroWebsiteSuggestions = true"
                      @blur="hideHeroWebsiteSuggestions"
                      :class="[
                        'w-full px-4 py-2 border rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-colors duration-200',
                        heroWebsiteImageNameError ? 'border-red-500 dark:border-red-500' : 'border-gray-300 dark:border-gray-600'
                      ]"
                      placeholder="m-drei"
                      autocomplete="off"
                    />
                    <div
                      v-if="showHeroWebsiteSuggestions && heroWebsiteImageNameSuggestions.length > 0"
                      class="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-48 overflow-auto"
                    >
                      <button
                        v-for="suggestion in heroWebsiteImageNameSuggestions"
                        :key="suggestion"
                        type="button"
                        @click="selectHeroWebsiteImageName(suggestion)"
                        class="w-full text-left px-4 py-2 text-sm text-gray-900 dark:text-gray-100 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                      >
                        {{ suggestion }}
                      </button>
                    </div>
                  </div>
                  <p v-if="heroWebsiteImageNameError" class="mt-1 text-sm text-red-600 dark:text-red-400">
                    {{ heroWebsiteImageNameError }}
                  </p>
                  <p v-else-if="heroWebsiteImageName && validateImageName(heroWebsiteImageName).valid" class="mt-1 text-xs text-green-600 dark:text-green-400">
                    ✓ Gültiger Bildname
                  </p>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Anzeige unter dem Hero-Bild
                    <span class="text-xs text-gray-500 dark:text-gray-400 font-normal">(optional)</span>
                  </label>
                  <input
                    v-model="heroWebsiteDisplayTitle"
                    type="text"
                    class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-colors duration-200"
                    placeholder="M-Drei Möbelfertigteile"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Projekt- oder Kundenlink
                    <span class="text-xs text-gray-500 dark:text-gray-400 font-normal">(optional)</span>
                  </label>
                  <input
                    v-model="heroWebsiteLinkUrl"
                    type="text"
                    class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-colors duration-200"
                    placeholder="/projects/m-drei oder https://kunde.de"
                  />
                </div>
                <div>
                  <input
                    ref="heroWebsiteImageInput"
                    type="file"
                    accept="image/*"
                    @change="(e) => handleImageUpload(e, 'hero-website')"
                    class="hidden"
                  />
                  <button
                    type="button"
                    @click="() => { if (heroWebsiteImageInput) heroWebsiteImageInput.click() }"
                    :disabled="uploadingHeroWebsite"
                    class="w-full border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-md px-4 py-8 text-center hover:border-blue-500 dark:hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg class="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    <p class="mt-2 text-sm text-gray-600 dark:text-gray-400">
                      <span v-if="uploadingHeroWebsite">Wird hochgeladen...</span>
                      <span v-else>Website-Bild für Hero hochladen</span>
                    </p>
                  </button>
                </div>
              </div>
            </div>

          </div>

          <!-- Batch-Konvertierung -->
          <div class="mt-8 mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <div class="flex items-center justify-between">
              <div>
                <h3 class="text-lg font-semibold text-blue-900 dark:text-blue-300 mb-1">Bilder zu WebP konvertieren</h3>
                <p class="text-sm text-blue-700 dark:text-blue-400">
                  Konvertiert alle bestehenden Bilder zu WebP für bessere Kompression und schnellere Ladezeiten.
                </p>
                <p v-if="convertResult" class="text-xs text-blue-600 dark:text-blue-500 mt-2">
                  Letzte Konvertierung: {{ convertResult.converted }} konvertiert, 
                  {{ convertResult.skipped }} übersprungen, 
                  {{ convertResult.failed }} fehlgeschlagen
                </p>
              </div>
              <button
                @click="handleBatchConvertImages"
                :disabled="convertingImages || loadingImages"
                class="px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-md font-medium hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
              >
                <span v-if="convertingImages">
                  Konvertiere... ({{ convertProgress.current }}/{{ convertProgress.total }})
                </span>
                <span v-else>Jetzt konvertieren</span>
              </button>
            </div>
            <div v-if="convertingImages && convertProgress.total > 0" class="mt-4">
              <div class="w-full bg-blue-200 dark:bg-blue-800 rounded-full h-2.5">
                <div 
                  class="bg-blue-600 dark:bg-blue-400 h-2.5 rounded-full transition-all duration-300"
                  :style="{ width: `${(convertProgress.current / convertProgress.total) * 100}%` }"
                ></div>
              </div>
              <p class="text-xs text-blue-700 dark:text-blue-400 mt-2 text-center">
                {{ convertProgress.imageName }}
              </p>
            </div>
          </div>

          <!-- Hochgeladene Bilder -->
          <div class="mt-8">
            <h3 class="text-xl font-semibold text-gray-900 dark:text-white mb-4">Hochgeladene Bilder</h3>
            
            <div v-if="loadingImages" class="text-center py-12">
              <p class="text-gray-600 dark:text-gray-400">Lade Bilder...</p>
            </div>

            <div v-else-if="siteImages.length === 0" class="text-center py-12">
              <p class="text-gray-600 dark:text-gray-400">Noch keine Bilder hochgeladen.</p>
            </div>

            <div v-else class="grid md:grid-cols-3 gap-6">
              <!-- Home Bilder -->
              <div>
                <h4 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Home-Seite</h4>
                <div class="space-y-4">
                  <div
                    v-for="image in homeImages"
                    :key="image.id"
                    class="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-lg dark:hover:shadow-xl transition-all duration-200 bg-gray-50 dark:bg-gray-900/50"
                  >
                    <div class="flex items-start space-x-4">
                      <img
                        :src="image.url"
                        :alt="image.name"
                        class="w-24 h-24 object-cover rounded-md shadow-sm"
                      />
                      <div class="flex-1 min-w-0">
                        <div v-if="editingSiteImageName?.id === image.id" class="mb-2">
                          <input
                            v-model="editingSiteImageName.name"
                            type="text"
                            @keyup.enter="saveSiteImageName(image.id)"
                            @keyup.esc="cancelEditSiteImageName"
                            class="w-full px-2 py-1 text-sm border border-blue-300 dark:border-blue-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                            @blur="saveSiteImageName(image.id)"
                          />
                        </div>
                        <p 
                          v-else
                          class="text-sm font-medium text-gray-900 dark:text-white truncate cursor-pointer hover:text-blue-600 dark:hover:text-blue-400"
                          @click="startEditSiteImageName(image.id, image.name)"
                          title="Klicken zum Bearbeiten"
                        >
                          {{ image.name }}
                        </p>
                        <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">{{ image.category }}</p>
                        <div class="mt-2">
                          <input
                            type="text"
                            :value="image.url"
                            readonly
                            class="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 cursor-pointer"
                            @click="(e) => (e.target as HTMLInputElement)?.select()"
                          />
                        </div>
                      </div>
                      <button
                        @click="handleDeleteImage(image)"
                        class="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 p-2 transition-colors"
                        title="Bild löschen"
                      >
                        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  <div v-if="homeImages.length === 0" class="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                    Keine Bilder für Home-Seite
                  </div>
                </div>
              </div>

              <!-- Über-mich Bilder -->
              <div>
                <h4 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Über-mich-Seite</h4>
                <div class="space-y-4">
                  <div
                    v-for="image in aboutImages"
                    :key="image.id"
                    class="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-lg dark:hover:shadow-xl transition-all duration-200 bg-gray-50 dark:bg-gray-900/50"
                  >
                    <div class="flex items-start space-x-4">
                      <img
                        :src="image.url"
                        :alt="image.name"
                        class="w-24 h-24 object-cover rounded-md shadow-sm"
                      />
                      <div class="flex-1 min-w-0">
                        <div v-if="editingSiteImageName?.id === image.id" class="mb-2">
                          <input
                            v-model="editingSiteImageName.name"
                            type="text"
                            @keyup.enter="saveSiteImageName(image.id)"
                            @keyup.esc="cancelEditSiteImageName"
                            class="w-full px-2 py-1 text-sm border border-blue-300 dark:border-blue-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                            @blur="saveSiteImageName(image.id)"
                          />
                        </div>
                        <p 
                          v-else
                          class="text-sm font-medium text-gray-900 dark:text-white truncate cursor-pointer hover:text-blue-600 dark:hover:text-blue-400"
                          @click="startEditSiteImageName(image.id, image.name)"
                          title="Klicken zum Bearbeiten"
                        >
                          {{ image.name }}
                        </p>
                        <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">{{ image.category }}</p>
                        <div class="mt-2">
                          <input
                            type="text"
                            :value="image.url"
                            readonly
                            class="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 cursor-pointer"
                            @click="(e) => (e.target as HTMLInputElement)?.select()"
                          />
                        </div>
                      </div>
                      <button
                        @click="handleDeleteImage(image)"
                        class="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 p-2 transition-colors"
                        title="Bild löschen"
                      >
                        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  <div v-if="aboutImages.length === 0" class="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                    Keine Bilder für Über-mich-Seite
                  </div>
                </div>
              </div>

              <!-- Hero-Website Bilder -->
              <div>
                <h4 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Hero-Websites</h4>
                <div class="space-y-4">
                  <div
                    v-for="image in heroWebsiteImages"
                    :key="image.id"
                    class="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-lg dark:hover:shadow-xl transition-all duration-200 bg-gray-50 dark:bg-gray-900/50"
                  >
                    <div class="flex items-start space-x-4">
                      <img
                        :src="image.url"
                        :alt="image.name"
                        class="w-24 h-24 object-cover object-top rounded-md shadow-sm"
                      />
                      <div class="flex-1 min-w-0">
                        <div v-if="editingSiteImageName?.id === image.id" class="mb-2">
                          <input
                            v-model="editingSiteImageName.name"
                            type="text"
                            @keyup.enter="saveSiteImageName(image.id)"
                            @keyup.esc="cancelEditSiteImageName"
                            class="w-full px-2 py-1 text-sm border border-blue-300 dark:border-blue-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                            @blur="saveSiteImageName(image.id)"
                          />
                        </div>
                        <p
                          v-else
                          class="text-sm font-medium text-gray-900 dark:text-white truncate cursor-pointer hover:text-blue-600 dark:hover:text-blue-400"
                          @click="startEditSiteImageName(image.id, image.name)"
                          title="Klicken zum Bearbeiten"
                        >
                          {{ image.name }}
                        </p>
                        <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">{{ image.category }}</p>
                        <div class="mt-2 grid gap-2">
                          <input
                            type="text"
                            :value="image.displayTitle || ''"
                            placeholder="Anzeige, z.B. M-Drei Möbelfertigteile"
                            class="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                            @blur="(e) => saveHeroWebsiteImageDetails(image, 'displayTitle', e)"
                          />
                          <input
                            type="text"
                            :value="image.linkUrl || ''"
                            placeholder="Link, z.B. /projects/m-drei oder https://kunde.de"
                            class="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                            @blur="(e) => saveHeroWebsiteImageDetails(image, 'linkUrl', e)"
                          />
                        </div>
                        <div class="mt-2">
                          <input
                            type="text"
                            :value="image.url"
                            readonly
                            class="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 cursor-pointer"
                            @click="(e) => (e.target as HTMLInputElement)?.select()"
                          />
                        </div>
                      </div>
                      <button
                        @click="handleDeleteImage(image)"
                        class="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 p-2 transition-colors"
                        title="Bild löschen"
                      >
                        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  <div v-if="heroWebsiteImages.length === 0" class="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                    Keine Hero-Website-Bilder
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>

        <!-- Projekte Tab -->
        <div v-if="activeTab === 'projects'">
          <div class="flex justify-between items-center mb-8">
            <div>
              <h2 class="text-2xl font-bold text-gray-900 dark:text-white">Projekte verwalten</h2>
              <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {{ favoriteProjects.length }} Favoriten | {{ regularProjects.length }} weitere Projekte
              </p>
            </div>
            <button
              @click="openCreateModal"
              class="inline-flex items-center px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-lg font-bold hover:bg-blue-700 dark:hover:bg-blue-600 transition-all shadow-lg shadow-blue-500/30"
            >
              <PlusIcon class="w-5 h-5 mr-2" />
              Neues Projekt
            </button>
          </div>

          <div v-if="loading" class="text-center py-20">
            <div class="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
            <p class="mt-4 text-gray-600 dark:text-gray-400 font-medium">Projekte werden geladen...</p>
          </div>

          <div v-else class="space-y-12">
            <!-- Favoriten Section -->
            <section>
              <div class="flex items-center space-x-3 mb-6">
                <div class="p-2 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
                  <StarIconSolid class="w-6 h-6 text-yellow-500" />
                </div>
                <div>
                  <h3 class="text-xl font-bold text-gray-900 dark:text-white">Favoriten</h3>
                  <p class="text-xs text-gray-500 dark:text-gray-400">Diese Projekte werden auf der Startseite angezeigt. Ziehen zum Sortieren.</p>
                </div>
              </div>

              <div v-if="favoriteProjects.length === 0">
                <EmptyState
                  :icon="StarIconSolid"
                  title="Keine Favoriten"
                  message="Markiere Projekte als Favoriten, damit sie auf der Startseite erscheinen."
                />
              </div>
              <div 
                v-else 
                class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 p-4 bg-gray-50/50 dark:bg-gray-900/20 rounded-2xl border border-gray-100 dark:border-gray-800"
              >
                <DraggableProjectCard
                  v-for="(project, index) in favoriteProjects"
                  :key="project.id || project.title"
                  :project="project"
                  :is-dragging="favDnD.draggedItemIndex.value === index"
                  @drag-start="favDnD.handleDragStart(index)"
                  @drag-over="favDnD.handleDragOver($event, index)"
                  @drop="favDnD.handleDrop()"
                  @drag-end="favDnD.handleDragEnd()"
                  @edit="openEditModal(project)"
                  @delete="handleDelete(project.id!, project.screenshots, project.logoLight, project.logoDark, project.gallery)"
                  @toggle-favorite="handleToggleFavorite(project)"
                />
              </div>
            </section>

            <!-- Weitere Projekte Section -->
            <section>
              <div class="flex items-center space-x-3 mb-6">
                <div class="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                  <FolderIcon class="w-6 h-6 text-blue-500" />
                </div>
                <div>
                  <h3 class="text-xl font-bold text-gray-900 dark:text-white">Weitere Projekte</h3>
                  <p class="text-xs text-gray-500 dark:text-gray-400">Diese Projekte erscheinen nur auf der Portfolio-Seite. Ziehen zum Sortieren.</p>
                </div>
              </div>

              <div v-if="regularProjects.length === 0">
                <EmptyState
                  :icon="FolderIcon"
                  title="Keine weiteren Projekte"
                  message="Du hast noch keine weiteren Projekte angelegt."
                  action-text="Erstes Projekt erstellen"
                  @action="openCreateModal"
                />
              </div>
              <div 
                v-else 
                class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 p-4 bg-gray-50/50 dark:bg-gray-900/20 rounded-2xl border border-gray-100 dark:border-gray-800"
              >
                <DraggableProjectCard
                  v-for="(project, index) in regularProjects"
                  :key="project.id || project.title"
                  :project="project"
                  :is-dragging="regDnD.draggedItemIndex.value === index"
                  @drag-start="regDnD.handleDragStart(index)"
                  @drag-over="regDnD.handleDragOver($event, index)"
                  @drop="regDnD.handleDrop()"
                  @drag-end="regDnD.handleDragEnd()"
                  @edit="openEditModal(project)"
                  @delete="handleDelete(project.id!, project.screenshots, project.logoLight, project.logoDark, project.gallery)"
                  @toggle-favorite="handleToggleFavorite(project)"
                />
              </div>
            </section>
          </div>
        </div>

        <!-- Kontaktformulare Tab - Submissions -->
        <div v-if="activeTab === 'contact-form' && activeContactFormSubTab === 'submissions'">
          <div class="mb-6">
            <h2 class="text-2xl font-bold text-gray-900 dark:text-white mb-2">Kontaktformular-Submissions</h2>
            <p class="text-gray-600 dark:text-gray-400">
              Übersicht aller eingegangenen Kontaktanfragen. Vollständige Daten für Backup-Zwecke (falls E-Mail nicht ankommt).
            </p>
          </div>

          <!-- Filter und Suche -->
          <div class="mb-6 flex flex-wrap gap-4 items-center">
            <div class="flex-1 min-w-[200px]">
              <input
                v-model="submissionSearch"
                type="text"
                placeholder="Suche nach Name, E-Mail oder Nachricht..."
                class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400"
              />
            </div>
            <select
              v-model="submissionFilter"
              class="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400"
            >
              <option value="all">Alle</option>
              <option value="unread">Ungelesen</option>
              <option value="processed">Bearbeitet</option>
              <option value="spam">Nur Spam</option>
              <option value="not-spam">Kein Spam</option>
            </select>
            <button
              @click="loadSubmissions"
              :disabled="loadingSubmissions"
              class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 dark:hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <span v-if="loadingSubmissions">Lädt...</span>
              <span v-else class="flex items-center gap-2">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Aktualisieren
              </span>
            </button>
          </div>

          <!-- Bulk-Aktionen -->
          <div v-if="filteredSubmissions.length > 0" class="mb-4 flex flex-wrap gap-3 items-center p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-700">
            <div class="flex items-center gap-2">
              <input
                type="checkbox"
                :checked="allSelected"
                @change="toggleSelectAll"
                class="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-400 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
              />
              <span class="text-sm text-gray-700 dark:text-gray-300">
                {{ selectedSubmissions.length }} von {{ filteredSubmissions.length }} ausgewählt
              </span>
            </div>
            <div class="flex-1"></div>
            <div class="flex gap-2">
              <button
                v-if="selectedSubmissions.length > 0"
                @click="bulkMarkAsProcessed"
                :disabled="processingBulk"
                class="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 dark:hover:bg-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
              >
                <span v-if="processingBulk">Wird verarbeitet...</span>
                <span v-else class="flex items-center gap-2">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                  </svg>
                  Als bearbeitet markieren
                </span>
              </button>
              <button
                v-if="selectedSubmissions.length > 0"
                @click="bulkDelete"
                :disabled="processingBulk"
                class="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 dark:hover:bg-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
              >
                <span v-if="processingBulk">Wird gelöscht...</span>
                <span v-else class="flex items-center gap-2">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Ausgewählte löschen
                </span>
              </button>
              <button
                @click="deleteAllSubmissions"
                :disabled="processingBulk || submissions.length === 0"
                class="px-4 py-2 bg-red-700 text-white rounded-md hover:bg-red-800 dark:hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
              >
                <span class="flex items-center gap-2">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Alle löschen
                </span>
              </button>
            </div>
          </div>

          <!-- Submissions Liste -->
          <div v-if="loadingSubmissions" class="text-center py-12">
            <p class="text-gray-600 dark:text-gray-400">Lade Submissions...</p>
          </div>

          <div v-else-if="filteredSubmissions.length === 0" class="text-center py-12 border border-gray-200 dark:border-gray-700 rounded-lg">
            <p class="text-gray-600 dark:text-gray-400">Keine Submissions gefunden.</p>
          </div>

          <div v-else class="space-y-4">
            <div
              v-for="submission in filteredSubmissions"
              :key="submission.id"
              :class="[
                'border rounded-lg p-6 transition-colors',
                submission.isSpam
                  ? 'border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20'
                  : submission.isProcessed
                    ? 'border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50 opacity-75'
                    : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
              ]"
            >
              <div class="flex justify-between items-start mb-4">
                <div class="flex items-start gap-3 flex-1">
                  <input
                    type="checkbox"
                    :checked="selectedSubmissions.includes(submission.id)"
                    @change="toggleSelection(submission.id)"
                    class="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-400 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                  />
                  <div class="flex-1">
                    <div class="flex items-center gap-3 mb-2">
                      <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
                        {{ submission.name }}
                      </h3>
                      <span
                        v-if="submission.isSpam"
                        class="px-2 py-1 text-xs font-semibold text-red-800 dark:text-red-200 bg-red-200 dark:bg-red-800 rounded"
                      >
                        SPAM
                      </span>
                      <span
                        v-if="submission.isProcessed"
                        class="px-2 py-1 text-xs font-semibold text-green-800 dark:text-green-200 bg-green-200 dark:bg-green-800 rounded"
                      >
                        BEARBEITET
                      </span>
                      <span
                        v-else-if="!submission.isRead"
                        class="px-2 py-1 text-xs font-semibold text-blue-800 dark:text-blue-200 bg-blue-200 dark:bg-blue-800 rounded"
                      >
                        NEU
                      </span>
                    </div>
                    <div class="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                      <p><strong>E-Mail:</strong> {{ submission.email }}</p>
                      <p><strong>Datum:</strong> {{ formatDate(submission.submittedAt) }}</p>
                      <p v-if="submission.interest"><strong>Interessensgebiet:</strong> {{ getInterestLabel(submission.interest) }}</p>
                      <p v-if="submission.projectType">
                        <strong>Projekttyp:</strong> {{ getProjectTypeLabel(submission.projectType) }}
                      </p>
                      <p v-if="submission.budget">
                        <strong>Budget:</strong> {{ getBudgetLabel(submission.budget) }}
                      </p>
                    </div>
                  </div>
                </div>
                <div class="flex gap-2">
                  <button
                    v-if="!submission.isProcessed"
                    @click="markAsProcessed(submission.id)"
                    class="p-2 text-gray-600 dark:text-gray-400 hover:text-green-600 dark:hover:text-green-400 transition-colors"
                    title="Als bearbeitet markieren"
                  >
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </button>
                  <button
                    v-else
                    @click="markAsUnread(submission.id)"
                    class="p-2 text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                    title="Als ungelesen markieren"
                  >
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </button>
                  <button
                    @click="copySubmissionEmail(submission.email)"
                    class="p-2 text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                    title="E-Mail kopieren"
                  >
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </button>
                  <button
                    v-if="!submission.isSpam"
                    @click="markAsSpam(submission.id)"
                    class="p-2 text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                    title="Als Spam markieren"
                  >
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </button>
                  <button
                    @click="deleteSubmission(submission.id)"
                    class="p-2 text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 transition-colors"
                    title="Löschen"
                  >
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
              <div class="mt-4 p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
                <p class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Nachricht:</p>
                <p class="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap">{{ submission.message }}</p>
              </div>
            </div>
          </div>

          <!-- Statistik -->
          <div v-if="submissions.length > 0" class="mt-8 grid grid-cols-1 md:grid-cols-5 gap-4">
            <div class="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-white dark:bg-gray-800">
              <p class="text-sm text-gray-600 dark:text-gray-400">Gesamt</p>
              <p class="text-2xl font-bold text-gray-900 dark:text-white">{{ submissions.length }}</p>
            </div>
            <div class="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-white dark:bg-gray-800">
              <p class="text-sm text-gray-600 dark:text-gray-400">Ungelesen</p>
              <p class="text-2xl font-bold text-blue-600 dark:text-blue-400">
                {{ submissions.filter(s => !s.isRead).length }}
              </p>
            </div>
            <div class="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-white dark:bg-gray-800">
              <p class="text-sm text-gray-600 dark:text-gray-400">Bearbeitet</p>
              <p class="text-2xl font-bold text-green-600 dark:text-green-400">
                {{ submissions.filter(s => s.isProcessed).length }}
              </p>
            </div>
            <div class="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-white dark:bg-gray-800">
              <p class="text-sm text-gray-600 dark:text-gray-400">Kein Spam</p>
              <p class="text-2xl font-bold text-gray-600 dark:text-gray-400">
                {{ submissions.filter(s => !s.isSpam).length }}
              </p>
            </div>
            <div class="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-white dark:bg-gray-800">
              <p class="text-sm text-gray-600 dark:text-gray-400">Spam</p>
              <p class="text-2xl font-bold text-red-600 dark:text-red-400">
                {{ submissions.filter(s => s.isSpam).length }}
              </p>
            </div>
          </div>
        </div>

        <!-- Einstellungen Tab - Kontakt -->
        <div v-if="activeTab === 'settings' && activeSubTab === 'contact'">
          <ContactInfoEditor />
        </div>

        <!-- Einstellungen Tab - Webseiten Einstellungen -->
        <div v-if="activeTab === 'settings' && activeSubTab === 'website'">
          <div class="mb-6">
            <h2 class="text-2xl font-bold text-gray-900 dark:text-white mb-2">Website-Einstellungen</h2>
            <p class="text-gray-600 dark:text-gray-400">Verwalten Sie die Einstellungen Ihrer Website</p>
          </div>

          <!-- Favicon-Verwaltung -->
          <div class="border border-gray-200 dark:border-gray-700 rounded-lg p-6 bg-gray-50 dark:bg-gray-900/50">
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Favicon</h3>
            <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Das Favicon wird im Browser-Tab angezeigt. Unterstützte Formate: ICO, PNG, SVG, JPG, WEBP
            </p>

            <!-- Aktuelles Favicon -->
            <div v-if="loadingFavicon" class="text-center py-8">
              <p class="text-gray-600 dark:text-gray-400">Lade Favicon...</p>
            </div>

            <div v-else-if="currentFaviconUrl" class="mb-6">
              <div class="flex items-center space-x-4 p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800">
                <div class="flex-shrink-0">
                  <img
                    :src="currentFaviconUrl"
                    alt="Aktuelles Favicon"
                    class="w-16 h-16 object-contain"
                  />
                </div>
                <div class="flex-1 min-w-0">
                  <p class="text-sm font-medium text-gray-900 dark:text-white mb-1">Aktuelles Favicon</p>
                  <input
                    type="text"
                    :value="currentFaviconUrl"
                    readonly
                    class="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 cursor-pointer"
                    @click="(e) => (e.target as HTMLInputElement)?.select()"
                  />
                </div>
                <button
                  @click="handleDeleteFavicon"
                  class="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 p-2 transition-colors"
                  title="Favicon löschen"
                >
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>

            <div v-else class="mb-6 p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800">
              <p class="text-sm text-gray-600 dark:text-gray-400">Kein Favicon hochgeladen. Verwenden Sie das Standard-Favicon.</p>
            </div>

            <!-- Favicon Upload -->
            <div>
              <input
                ref="faviconInput"
                type="file"
                accept="image/*,.ico"
                @change="handleFaviconUpload"
                class="hidden"
              />
              <button
                type="button"
                @click="() => { if (faviconInput) faviconInput.click() }"
                :disabled="uploadingFavicon"
                class="w-full border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-md px-4 py-8 text-center hover:border-blue-500 dark:hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg class="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <p class="mt-2 text-sm text-gray-600 dark:text-gray-400">
                  <span v-if="uploadingFavicon">Wird hochgeladen...</span>
                  <span v-else>{{ currentFaviconUrl ? 'Favicon ersetzen' : 'Favicon hochladen' }}</span>
                </p>
              </button>
            </div>
          </div>

          <!-- Share-Bild-Verwaltung -->
          <div class="border border-gray-200 dark:border-gray-700 rounded-lg p-6 bg-gray-50 dark:bg-gray-900/50 mt-6">
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Share-Bild (Open Graph)</h3>
            <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Das Share-Bild wird beim Teilen der Website auf Social Media Plattformen (Facebook, Twitter, LinkedIn, etc.) angezeigt. 
              Empfohlene Größe: 1200x630 Pixel. Unterstützte Formate: PNG, JPG, WEBP
            </p>

            <!-- Aktuelles Share-Bild -->
            <div v-if="loadingShareImage" class="text-center py-8">
              <p class="text-gray-600 dark:text-gray-400">Lade Share-Bild...</p>
            </div>

            <div v-else-if="currentShareImageUrl" class="mb-6">
              <div class="flex items-center space-x-4 p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800">
                <div class="flex-shrink-0">
                  <img
                    :src="currentShareImageUrl"
                    alt="Aktuelles Share-Bild"
                    class="w-32 h-20 object-cover rounded-md shadow-sm"
                  />
                </div>
                <div class="flex-1 min-w-0">
                  <p class="text-sm font-medium text-gray-900 dark:text-white mb-1">Aktuelles Share-Bild</p>
                  <input
                    type="text"
                    :value="currentShareImageUrl"
                    readonly
                    class="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 cursor-pointer"
                    @click="(e) => (e.target as HTMLInputElement)?.select()"
                  />
                </div>
                <button
                  @click="handleDeleteShareImage"
                  class="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 p-2 transition-colors"
                  title="Share-Bild löschen"
                >
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>

            <div v-else class="mb-6 p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800">
              <p class="text-sm text-gray-600 dark:text-gray-400">Kein Share-Bild hochgeladen. Es wird das Standard-Bild verwendet.</p>
            </div>

            <!-- Info-Box für automatische Bildanpassung -->
            <div class="mb-4 p-4 border border-blue-200 dark:border-blue-800 rounded-lg bg-blue-50 dark:bg-blue-900/20">
              <div class="flex items-start space-x-3">
                <svg class="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div class="flex-1">
                  <p class="text-sm font-medium text-blue-800 dark:text-blue-200 mb-1">Automatische Bildoptimierung</p>
                  <ul class="text-xs text-blue-700 dark:text-blue-300 space-y-1">
                    <li>✓ Wird automatisch auf 1200×630 Pixel angepasst (Open Graph Standard)</li>
                    <li>✓ Weißer Hintergrund wird bei Transparenz hinzugefügt</li>
                    <li>✓ Bild wird zentriert mit Padding dargestellt</li>
                    <li>✓ Optimiert für Facebook, Twitter, LinkedIn & Co.</li>
                  </ul>
                </div>
              </div>
            </div>

            <!-- Share-Bild Upload -->
            <div>
              <input
                ref="shareImageInput"
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/webp"
                @change="handleShareImageUpload"
                class="hidden"
              />
              <button
                type="button"
                @click="() => { if (shareImageInput) shareImageInput.click() }"
                :disabled="uploadingShareImage"
                class="w-full border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-md px-4 py-8 text-center hover:border-blue-500 dark:hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg class="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <p class="mt-2 text-sm text-gray-600 dark:text-gray-400">
                  <span v-if="uploadingShareImage">Wird verarbeitet und hochgeladen...</span>
                  <span v-else>{{ currentShareImageUrl ? 'Share-Bild ersetzen' : 'Share-Bild hochladen' }}</span>
                </p>
                <p class="mt-1 text-xs text-gray-500 dark:text-gray-500">PNG, JPG oder WEBP (max. 8 MB)</p>
              </button>
            </div>
          </div>
        </div>

        <!-- Testimonials Tab -->
        <div v-if="activeTab === 'testimonials'">
          <TestimonialsEditor />
        </div>

        <!-- Kontaktformulare Tab - E-Mail Admin Benachrichtigung -->
        <div v-if="activeTab === 'contact-form' && activeContactFormSubTab === 'email-notification'">
          <AdminSettingsEditor />
        </div>

        <!-- Kontaktformulare Tab - E-Mail Templates -->
        <div v-if="activeTab === 'contact-form' && activeContactFormSubTab === 'templates'">
          <EmailTemplateEditor />
        </div>
      </div>
    </div>

    <!-- Create/Edit Modal -->
    <Teleport to="body">
      <Transition
        enter-active-class="transition-opacity duration-300"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition-opacity duration-200"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          v-if="showModal"
          class="fixed inset-0 z-[9998] flex items-center justify-center p-4"
          @click.self="closeModal"
        >
          <!-- Backdrop -->
          <div
            class="absolute inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 transition-opacity"
            @click="closeModal"
          ></div>

          <!-- Modal Content -->
          <Transition
            enter-active-class="transition-all duration-300"
            enter-from-class="opacity-0 scale-95 translate-y-4"
            enter-to-class="opacity-100 scale-100 translate-y-0"
            leave-active-class="transition-all duration-200"
            leave-from-class="opacity-100 scale-100 translate-y-0"
            leave-to-class="opacity-0 scale-95 translate-y-4"
          >
            <div
              v-if="showModal"
              class="relative bg-white dark:bg-gray-800 rounded-lg max-w-5xl w-full max-h-[90vh] overflow-y-auto shadow-2xl border border-gray-200 dark:border-gray-700 transition-colors duration-300"
              role="dialog"
              aria-modal="true"
              aria-labelledby="project-modal-title"
            >
              <div class="p-6">
                <div class="flex justify-between items-center mb-6">
                  <h3
                    id="project-modal-title"
                    class="text-2xl font-bold text-gray-900 dark:text-white"
                  >
                    {{ editingProject ? 'Projekt bearbeiten' : 'Neues Projekt erstellen' }}
                  </h3>
                  <button
                    @click="closeModal"
                    class="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
                    aria-label="Schließen"
                  >
                    <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <!-- Modal Tabs -->
                <div class="border-b border-gray-200 dark:border-gray-700 mb-6">
                  <nav class="flex space-x-8" aria-label="Modal Tabs">
                    <button
                      v-for="tab in [
                        { id: 'description', name: 'Beschreibung' },
                        { id: 'branding', name: 'Branding und Design' },
                        { id: 'media', name: 'Bilder und Screenshots' }
                      ]"
                      :key="tab.id"
                      @click="activeModalTab = tab.id"
                      type="button"
                      :class="[
                        'py-4 px-1 border-b-2 font-medium text-sm transition-all duration-200',
                        activeModalTab === tab.id
                          ? 'border-blue-500 dark:border-blue-400 text-blue-600 dark:text-blue-400'
                          : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                      ]"
                    >
                      {{ tab.name }}
                    </button>
                  </nav>
                </div>

          <form @submit.prevent="handleSubmit" class="space-y-6">
            <!-- Tab: Beschreibung -->
            <div v-if="activeModalTab === 'description'" class="space-y-6 animate-fade-in">
              <div class="grid md:grid-cols-2 gap-6">
                <div class="space-y-6">
                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Titel *
                    </label>
                    <input
                      v-model="formData.title"
                      type="text"
                      required
                      class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-colors duration-200"
                      placeholder="Projekt-Titel"
                    />
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Beschreibung *
                    </label>
                    <textarea
                      v-model="formData.description"
                      required
                      rows="6"
                      class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-colors duration-200 resize-none"
                      placeholder="Projekt-Beschreibung"
                    ></textarea>
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Technologien (kommagetrennt)
                    </label>
                    <input
                      v-model="technologiesInput"
                      type="text"
                      class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-colors duration-200"
                      placeholder="React, Vue.js, TypeScript"
                    />
                  </div>
                </div>

                <div class="space-y-6">
                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Live URL
                    </label>
                    <input
                      v-model="formData.liveUrl"
                      type="url"
                      class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-colors duration-200"
                      placeholder="https://example.com"
                    />
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      GitHub URL
                    </label>
                    <input
                      v-model="formData.githubUrl"
                      type="url"
                      class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-colors duration-200"
                      placeholder="https://github.com/username/repo"
                    />
                  </div>

                  <div v-if="editingProject">
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Projektseite zuweisen
                    </label>
                    <select
                      v-model="formData.projectPageUrl"
                      @change="handleProjectPageChange"
                      class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-colors duration-200"
                    >
                      <option value="">Keine Projektseite</option>
                      <option
                        v-for="page in availableProjectPages"
                        :key="page.path"
                        :value="page.path"
                      >
                        {{ page.name }} ({{ page.path }})
                      </option>
                    </select>
                  </div>

                  <div class="flex items-center space-x-3 pt-4">
                    <input
                      v-model="formData.isFavorite"
                      type="checkbox"
                      id="isFavorite"
                      class="w-5 h-5 text-blue-600 dark:text-blue-400 border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 cursor-pointer"
                    />
                    <label for="isFavorite" class="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
                      Als Favorit markieren
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <!-- Tab: Branding und Design -->
            <div v-if="activeModalTab === 'branding'" class="space-y-8 animate-fade-in">
              <!-- Logos Section -->
              <div class="space-y-6">
                <h4 class="text-lg font-semibold text-gray-900 dark:text-white border-b border-gray-100 dark:border-gray-700 pb-2">Logos</h4>
                <div class="grid md:grid-cols-2 gap-8">
                  <!-- Light Mode Logo -->
                  <div class="bg-gray-50 dark:bg-gray-900/30 p-6 rounded-xl border border-gray-100 dark:border-gray-700">
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-4 text-center">
                      Logo (Light Mode)
                    </label>
                    <div class="flex justify-center">
                      <div v-if="formData.logoLight" class="relative group aspect-square w-40 bg-white rounded-lg overflow-hidden border border-gray-200 p-4 shadow-sm">
                        <img :src="formData.logoLight" class="w-full h-full object-contain" />
                        <button @click="removeLogo('light')" type="button" class="absolute top-2 right-2 bg-red-600 text-white p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-md">
                          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>
                      </div>
                      <button v-else @click="logoLightInput?.click()" type="button" class="w-40 h-40 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg flex flex-col items-center justify-center hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/10 transition-all group">
                        <PlusIcon class="w-8 h-8 text-gray-400 group-hover:text-blue-500 transition-colors" />
                        <span class="text-xs text-gray-500 mt-2">Upload Light</span>
                      </button>
                    </div>
                    <input ref="logoLightInput" type="file" class="hidden" @change="handleLogoUpload($event, 'light')" accept="image/*" />
                  </div>

                  <!-- Dark Mode Logo -->
                  <div class="bg-gray-50 dark:bg-gray-900/30 p-6 rounded-xl border border-gray-100 dark:border-gray-700">
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-4 text-center">
                      Logo (Dark Mode)
                    </label>
                    <div class="flex justify-center">
                      <div v-if="formData.logoDark" class="relative group aspect-square w-40 bg-gray-800 rounded-lg overflow-hidden border border-gray-700 p-4 shadow-sm">
                        <img :src="formData.logoDark" class="w-full h-full object-contain" />
                        <button @click="removeLogo('dark')" type="button" class="absolute top-2 right-2 bg-red-600 text-white p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-md">
                          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>
                      </div>
                      <button v-else @click="logoDarkInput?.click()" type="button" class="w-40 h-40 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg flex flex-col items-center justify-center hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/10 transition-all group">
                        <PlusIcon class="w-8 h-8 text-gray-400 group-hover:text-blue-500 transition-colors" />
                        <span class="text-xs text-gray-500 mt-2">Upload Dark</span>
                      </button>
                    </div>
                    <input ref="logoDarkInput" type="file" class="hidden" @change="handleLogoUpload($event, 'dark')" accept="image/*" />
                  </div>
                </div>
              </div>

              <!-- Consolidated Accent Color Section -->
              <div class="space-y-6">
                <h4 class="text-lg font-semibold text-gray-900 dark:text-white border-b border-gray-100 dark:border-gray-700 pb-2">Farben</h4>
                <div class="bg-gray-50 dark:bg-gray-900/30 p-6 rounded-xl border border-gray-100 dark:border-gray-700">
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
                    Feature-Farbe (Akzentfarbe)
                    <span class="text-xs text-gray-500 dark:text-gray-400 font-normal ml-2">Wird für Rahmen und Links verwendet</span>
                  </label>
                  <div class="flex items-center space-x-6">
                    <div class="flex flex-col items-center space-y-2">
                      <input 
                        :value="normalizedColor"
                        @input="handleColorPickerChange"
                        type="color" 
                        class="h-12 w-20 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 p-1 cursor-pointer shadow-sm" 
                      />
                      <span class="text-[10px] text-gray-500 uppercase font-bold">Wählen</span>
                    </div>
                    <div class="flex-1 space-y-4">
                      <div class="relative">
                        <input
                          v-model="accentColorInput"
                          @input="handleHexInput"
                          @blur="validateAndNormalizeColor"
                          @keyup.enter="validateAndNormalizeColor"
                          type="text"
                          class="w-full px-4 py-2 border rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-colors duration-200 font-mono text-sm"
                          :class="accentColorError ? 'border-red-500 dark:border-red-500' : 'border-gray-300 dark:border-gray-600'"
                          placeholder="#3B82F6"
                        />
                        <div class="absolute right-3 top-1/2 -translate-y-1/2 flex items-center space-x-2">
                          <button @click="resetAccentColor" type="button" class="text-xs text-blue-600 dark:text-blue-400 hover:underline">Reset</button>
                        </div>
                      </div>
                      <div class="flex items-center space-x-4">
                        <div :style="{ backgroundColor: normalizedColor }" class="h-10 w-20 rounded-lg border border-gray-200 dark:border-gray-700 shadow-inner"></div>
                        <div :style="{ borderColor: normalizedColor, color: normalizedColor }" class="px-6 py-2 border-2 rounded-xl text-sm font-bold shadow-sm">Beispiel Button</div>
                        <p v-if="accentColorError" class="text-xs text-red-600 dark:text-red-400 font-medium">{{ accentColorError }}</p>
                        <p v-else-if="formData.projectPageUrl && detectFeatureColor(formData.projectPageUrl)" class="text-xs text-green-600 dark:text-green-400 font-medium">✓ Erkannt</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Hinweis für komplexe Projekte (in Branding Tab) -->
              <div v-if="formData.projectPageUrl" class="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <p class="text-sm text-blue-800 dark:text-blue-300">
                  <span class="font-bold">Hinweis:</span> Dieses Projekt hat eine eigene Detailseite. Logo und Farbe werden primär in der Vue-Datei verwaltet, können hier aber für die Startseite überschrieben werden.
                </p>
              </div>
            </div>

            <!-- Tab: Bilder und Screenshots -->
            <div v-if="activeModalTab === 'media'" class="space-y-8 animate-fade-in">
              <!-- Screenshots -->
              <div class="space-y-6">
                <h4 class="text-lg font-semibold text-gray-900 dark:text-white border-b border-gray-100 dark:border-gray-700 pb-2">Screenshots (Vorschau)</h4>
                <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  <div
                    v-for="(screenshot, index) in formData.screenshots"
                    :key="index"
                    class="relative group aspect-video bg-gray-100 dark:bg-gray-900 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 shadow-sm"
                  >
                    <img :src="screenshot" class="w-full h-full object-cover" />
                    <div class="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <button @click="removeScreenshot(index)" type="button" class="p-2 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors shadow-lg">
                        <TrashIcon class="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                  
                  <button @click="fileInput?.click()" type="button" class="aspect-video border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg flex flex-col items-center justify-center hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/10 transition-all group">
                    <PlusIcon class="w-8 h-8 text-gray-400 group-hover:text-blue-500 transition-colors" />
                    <span class="text-xs text-gray-500 mt-2 font-medium">Upload</span>
                  </button>
                  <input ref="fileInput" type="file" class="hidden" multiple @change="handleFileUpload" accept="image/*" />
                </div>
              </div>

              <!-- Gallery -->
              <div v-if="!formData.projectPageUrl" class="space-y-6">
                <h4 class="text-lg font-semibold text-gray-900 dark:text-white border-b border-gray-100 dark:border-gray-700 pb-2">Galerie-Medien (Bilder/Videos)</h4>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div v-for="(item, index) in formData.gallery" :key="index" class="flex items-start space-x-4 p-4 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-900/50 shadow-sm hover:shadow-md transition-shadow">
                    <div class="w-24 h-24 flex-shrink-0 bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
                      <video v-if="item.type === 'video'" :src="item.url" class="w-full h-full object-cover" />
                      <img v-else :src="item.url" class="w-full h-full object-cover" />
                    </div>
                    <div class="flex-1 space-y-3">
                      <input v-model="item.caption" type="text" placeholder="Beschriftung (optional)" class="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500/50" />
                      <div class="flex items-center space-x-2">
                        <span class="text-[10px] px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-500 uppercase font-bold">{{ item.type }}</span>
                        <div class="flex-1"></div>
                        <button @click="moveGalleryItem(index, 'up')" type="button" class="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-gray-500" :disabled="index === 0">
                          <ChevronUpIcon class="w-4 h-4" />
                        </button>
                        <button @click="moveGalleryItem(index, 'down')" type="button" class="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-gray-500" :disabled="index === formData.gallery.length - 1">
                          <ChevronDownIcon class="w-4 h-4" />
                        </button>
                        <button @click="removeGalleryItem(index)" type="button" class="p-1.5 hover:bg-red-50 text-red-600 rounded-lg transition-colors ml-2">
                          <TrashIcon class="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  <button @click="galleryInput?.click()" type="button" class="py-10 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl flex flex-col items-center justify-center hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/10 transition-all group h-full min-h-[120px]">
                    <PlusIcon class="w-10 h-10 text-gray-400 group-hover:text-blue-500 transition-colors" />
                    <span class="text-sm text-gray-500 mt-2 font-bold">Medien hinzufügen</span>
                  </button>
                  <input ref="galleryInput" type="file" class="hidden" multiple @change="handleGalleryUpload" accept="image/*,video/*" />
                </div>
              </div>

              <!-- Hinweis für komplexe Projekte (in Media Tab) -->
              <div v-else class="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <p class="text-sm text-blue-800 dark:text-blue-300">
                  <span class="font-bold">Hinweis:</span> Dieses Projekt hat eine eigene Detailseite. Die Galerie-Inhalte werden direkt in der Vue-Datei des Projekts verwaltet.
                </p>
              </div>
            </div>

            <div class="flex justify-end space-x-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                @click="closeModal"
                class="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors duration-200"
              >
                Abbrechen
              </button>
              <button
                type="submit"
                :disabled="submitting"
                class="px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-md font-medium hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
              >
                <span v-if="submitting">Wird gespeichert...</span>
                <span v-else>{{ editingProject ? 'Aktualisieren' : 'Erstellen' }}</span>
              </button>
            </div>
          </form>
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
    </Teleport>

    <!-- Modal Component -->
    <Modal
      :isOpen="modal.isOpen.value"
      @update:isOpen="(value) => modal.isOpen.value = value"
      :type="modal.modalOptions.value.type"
      :title="modal.modalOptions.value.title"
      :message="modal.modalOptions.value.message || ''"
      :confirmText="modal.modalOptions.value.confirmText"
      :cancelText="modal.modalOptions.value.cancelText"
      :showCloseButton="modal.modalOptions.value.showCloseButton"
      :closeOnBackdrop="modal.modalOptions.value.closeOnBackdrop"
      @confirm="modal.handleConfirm"
      @cancel="modal.handleCancel"
      @close="modal.handleClose"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { logout, getCurrentUser } from '../firebase/auth'
import Modal from '../components/Modal.vue'
import DraggableProjectCard from '../components/admin/DraggableProjectCard.vue'
import EmptyState from '../components/EmptyState.vue'
import ContactInfoEditor from '../components/admin/ContactInfoEditor.vue'
import EmailTemplateEditor from '../components/admin/EmailTemplateEditor.vue'
import AdminSettingsEditor from '../components/admin/AdminSettingsEditor.vue'
import TestimonialsEditor from '../components/admin/TestimonialsEditor.vue'
import { useModal } from '../composables/useModal'
import { useDragAndDrop } from '../composables/useDragAndDrop'
import { useErrorHandler } from '../composables/useErrorHandler'
import { logger } from '../utils/logger'
import APP_CONFIG from '../config/app'
import {
  getAllContactSubmissions,
  deleteContactSubmission,
  markSubmissionAsSpam,
  markSubmissionAsProcessed,
  markSubmissionAsUnread,
  markSubmissionsAsProcessed,
  deleteContactSubmissions,
  deleteAllContactSubmissions,
  type ContactSubmission
} from '../firebase/contact'
import { FolderIcon, StarIcon as StarIconSolid, PlusIcon } from '@heroicons/vue/24/solid'
import { TrashIcon, ChevronUpIcon, ChevronDownIcon } from '@heroicons/vue/24/outline'
import {
  getPortfolioProjects,
  createPortfolioProject,
  updatePortfolioProject,
  deletePortfolioProject,
  reorderProjects,
  toggleFavoriteStatus,
  uploadScreenshot,
  uploadProjectLogo,
  uploadGalleryMedia,
  type PortfolioProject
} from '../firebase/portfolio'
import { migrateProjectOrders } from '../utils/projectMigration'
import {
  uploadImage,
  deleteImage,
  getAllImages,
  updateImageName,
  updateImageDetails,
  batchConvertImagesToWebP,
  type SiteImage,
  type ImageCategory,
  type BatchConvertResult
} from '../firebase/images'
import {
  uploadFavicon,
  getFaviconUrl,
  deleteFavicon
} from '../firebase/favicon'
import {
  uploadShareImage,
  getShareImageUrl,
  deleteShareImage
} from '../firebase/shareImage'
import { getProjectPageRoutes } from '../router/index'

const router = useRouter()
const route = useRoute()
const modal = useModal()
const { handleError } = useErrorHandler()

// Haupttabs
const mainTabs = [
  { id: 'projects', name: 'Projekte', path: '/admin/projects' },
  { id: 'testimonials', name: 'Testimonials', path: '/admin/testimonials' },
  { id: 'settings', name: 'Einstellungen', path: '/admin/settings/images' },
  { id: 'contact-form', name: 'Kontaktformulare', path: '/admin/contact-form/submissions' }
]

// Untertabs für Einstellungen
const settingsSubTabs = [
  { id: 'images', name: 'Bilder', path: '/admin/settings/images' },
  { id: 'contact', name: 'Kontakt', path: '/admin/settings/contact' },
  { id: 'website', name: 'Webseiten Einstellungen', path: '/admin/settings/website' }
]

// Untertabs für Kontaktformulare
const contactFormSubTabs = [
  { id: 'submissions', name: 'Formular - Submissions', path: '/admin/contact-form/submissions' },
  { id: 'email-notification', name: 'E-Mail Admin Benachrichtigungen', path: '/admin/contact-form/email-notification' },
  { id: 'templates', name: 'E-Mail Templates', path: '/admin/contact-form/templates' }
]

// Tab-State aus Route ableiten
const activeTab = computed(() => {
  const adminTab = route.meta?.adminTab as string
  if (adminTab) return adminTab
  // Fallback basierend auf Pfad
  if (route.path.startsWith('/admin/projects')) return 'projects'
  if (route.path.startsWith('/admin/contact-form')) return 'contact-form'
  if (route.path.startsWith('/admin/settings')) return 'settings'
  return 'projects' // Standard
})

const activeSubTab = computed(() => {
  if (activeTab.value !== 'settings') return null
  const subtab = route.params.subtab as string
  if (subtab && ['images', 'contact', 'website'].includes(subtab)) {
    return subtab
  }
  return 'images' // Standard für Einstellungen
})

const activeContactFormSubTab = computed(() => {
  if (activeTab.value !== 'contact-form') return null
  const subtab = route.params.subtab as string
  if (subtab && ['submissions', 'email-notification', 'templates'].includes(subtab)) {
    return subtab
  }
  return 'submissions' // Standard für Kontaktformulare
})
const loading = ref(false)
const favoriteProjects = ref<PortfolioProject[]>([])
const regularProjects = ref<PortfolioProject[]>([])
const showModal = ref(false)
const activeModalTab = ref('description') // 'description', 'branding', 'media'
const editingProject = ref<PortfolioProject | null>(null)
const submitting = ref(false)
const fileInput = ref<HTMLInputElement | null>(null)
const logoLightInput = ref<HTMLInputElement | null>(null)
const logoDarkInput = ref<HTMLInputElement | null>(null)
const galleryInput = ref<HTMLInputElement | null>(null)

// Logo Upload Handlers
const handleLogoUpload = async (event: Event, mode: 'light' | 'dark') => {
  const target = event.target as HTMLInputElement
  const file = target.files?.[0]
  if (!file) return

  try {
    const projectId = editingProject.value?.id || 'temp-' + Date.now()
    const url = await uploadProjectLogo(file, projectId, mode)
    if (mode === 'light') formData.value.logoLight = url
    else formData.value.logoDark = url
  } catch (error) {
    handleError(error, `Fehler beim Hochladen des ${mode} Logos`)
  }
}

const removeLogo = (mode: 'light' | 'dark') => {
  if (mode === 'light') formData.value.logoLight = ''
  else formData.value.logoDark = ''
}

// Gallery Handlers
const handleGalleryUpload = async (event: Event) => {
  const target = event.target as HTMLInputElement
  const files = target.files
  if (!files || files.length === 0) return

  try {
    const projectId = editingProject.value?.id || 'temp-' + Date.now()

    for (let i = 0; i < files.length; i++) {
      const type = files[i].type.startsWith('video/') ? 'video' : 'image'
      const url = await uploadGalleryMedia(files[i], projectId, formData.value.gallery.length + i)
      formData.value.gallery.push({
        url,
        type,
        order: formData.value.gallery.length,
        caption: ''
      })
    }
  } catch (error) {
    handleError(error, 'Fehler beim Hochladen der Galerie-Medien')
  } finally {
    if (galleryInput.value) galleryInput.value.value = ''
  }
}

const removeGalleryItem = (index: number) => {
  formData.value.gallery.splice(index, 1)
  // Re-order
  formData.value.gallery.forEach((item, i) => {
    item.order = i
  })
}

const moveGalleryItem = (index: number, direction: 'up' | 'down') => {
  if (direction === 'up' && index > 0) {
    const item = formData.value.gallery[index]
    formData.value.gallery[index] = formData.value.gallery[index - 1]
    formData.value.gallery[index - 1] = item
  } else if (direction === 'down' && index < formData.value.gallery.length - 1) {
    const item = formData.value.gallery[index]
    formData.value.gallery[index] = formData.value.gallery[index + 1]
    formData.value.gallery[index + 1] = item
  }
  // Re-order
  formData.value.gallery.forEach((item, i) => {
    item.order = i
  })
}

// Bilder Tab State
const siteImages = ref<SiteImage[]>([])
const loadingImages = ref(false)
const uploadingHome = ref(false)
const uploadingAbout = ref(false)
const uploadingHeroWebsite = ref(false)
const homeImageName = ref('')
const aboutImageName = ref('')
const heroWebsiteImageName = ref('')
const heroWebsiteDisplayTitle = ref('')
const heroWebsiteLinkUrl = ref('')
const homeImageInput = ref<HTMLInputElement | null>(null)
const aboutImageInput = ref<HTMLInputElement | null>(null)
const heroWebsiteImageInput = ref<HTMLInputElement | null>(null)
const editingSiteImageName = ref<{ id: string; name: string } | null>(null)
const convertingImages = ref(false)
const convertProgress = ref({ current: 0, total: 0, imageName: '' })
const convertResult = ref<BatchConvertResult | null>(null)

// Einstellungen Tab State
const currentFaviconUrl = ref<string | null>(null)
const loadingFavicon = ref(false)
const uploadingFavicon = ref(false)
const faviconInput = ref<HTMLInputElement | null>(null)
const currentShareImageUrl = ref<string | null>(null)
const loadingShareImage = ref(false)
const uploadingShareImage = ref(false)
const shareImageInput = ref<HTMLInputElement | null>(null)

// Kontaktformular-Submissions
const submissions = ref<(ContactSubmission & { id: string })[]>([])
const loadingSubmissions = ref(false)
const submissionSearch = ref('')
const submissionFilter = ref<'all' | 'unread' | 'processed' | 'spam' | 'not-spam'>('all')
const selectedSubmissions = ref<string[]>([])
const processingBulk = ref(false)

const formData = ref({
  title: '',
  description: '',
  screenshots: [] as string[],
  technologies: [] as string[],
  liveUrl: '',
  githubUrl: '',
  projectPageUrl: '', // Optional: Route zur detaillierten Projektseite (z.B. '/projects/m-drei')
  logoLight: '',
  logoDark: '',
  accentColor: '#3B82F6',
  gallery: [] as { url: string; type: 'image' | 'video'; caption?: string; order: number }[],
  isFavorite: false // Standard: nicht favorisiert
})

// Feature-Farbe Input State
const accentColorInput = ref('#3B82F6')
const accentColorError = ref<string | null>(null)

// Normalisierte Farbe für Anzeige (immer mit # und 6-stellig)
const normalizedColor = computed(() => {
  return normalizeHexColor(formData.value.accentColor)
})

// HEX-Farbe normalisieren (mit # und 6-stellig)
const normalizeHexColor = (color: string): string => {
  if (!color) return '#3B82F6'
  
  // Entferne Leerzeichen
  color = color.trim()
  
  if (color === '') return '#3B82F6'
  
  // Wenn kein # vorhanden, füge es hinzu
  if (!color.startsWith('#')) {
    color = '#' + color
  }
  
  // Entferne # für Verarbeitung
  let hex = color.substring(1)
  
  // Wenn 3-stellig, expandiere zu 6-stellig (z.B. FFF -> FFFFFF)
  if (hex.length === 3) {
    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2]
  }
  
  // Stelle sicher, dass es 6-stellig ist und nur gültige HEX-Zeichen enthält
  if (hex.length === 6 && /^[0-9A-Fa-f]{6}$/.test(hex)) {
    return '#' + hex.toUpperCase()
  }
  
  // Fallback wenn ungültig
  return '#3B82F6'
}

// Validiere HEX-Code
const validateHexColor = (color: string, allowPartial: boolean = false): { valid: boolean; error?: string } => {
  if (!color || color.trim() === '') {
    return { valid: false, error: 'HEX-Code ist erforderlich' }
  }
  
  // Entferne # für Validierung
  let hex = color.replace('#', '').trim()
  
  // Wenn leer nach Entfernen von #
  if (hex === '') {
    return { valid: false, error: 'HEX-Code ist erforderlich' }
  }
  
  // Prüfe auf gültige HEX-Zeichen (0-9, A-F, a-f)
  // Erlaube teilweise Eingabe während der Eingabe (allowPartial)
  if (allowPartial) {
    // Während der Eingabe: erlaube 1-6 Zeichen, solange sie gültig sind
    if (!/^[0-9A-Fa-f]{1,6}$/.test(hex)) {
      return { valid: false, error: 'Ungültige Zeichen. Nur 0-9, A-F erlaubt.' }
    }
    // Wenn weniger als 3 Zeichen, ist es noch nicht vollständig, aber gültig
    if (hex.length < 3) {
      return { valid: true } // Noch nicht vollständig, aber gültig
    }
  }
  
  // Finale Validierung: muss genau 3 oder 6 Zeichen sein
  if (!/^[0-9A-Fa-f]{3}$|^[0-9A-Fa-f]{6}$/.test(hex)) {
    return { valid: false, error: 'Ungültiger HEX-Code. Verwende Format: #FFF oder #FFFFFF' }
  }
  
  return { valid: true }
}

// Handler für Color-Picker Änderung
const handleColorPickerChange = (event: Event) => {
  const target = event.target as HTMLInputElement
  formData.value.accentColor = target.value
  accentColorInput.value = target.value
  accentColorError.value = null
}

// Handler für HEX-Input
const handleHexInput = (event: Event) => {
  const target = event.target as HTMLInputElement
  accentColorInput.value = target.value
  accentColorError.value = null
  
  // Validiere während der Eingabe (erlaube teilweise Eingabe)
  const validation = validateHexColor(target.value, true)
  if (!validation.valid && target.value.trim() !== '') {
    accentColorError.value = validation.error || null
  } else if (validation.valid) {
    // Wenn vollständig gültig (3 oder 6 Zeichen), normalisiere und aktualisiere formData
    const hex = target.value.replace('#', '').trim()
    if (hex.length === 3 || hex.length === 6) {
      const normalized = normalizeHexColor(target.value)
      formData.value.accentColor = normalized
      // Aktualisiere Input nur wenn normalisiert anders ist (z.B. bei 3-stellig)
      if (normalized !== target.value && hex.length === 3) {
        // Bei 3-stelliger Eingabe: expandiere zu 6-stellig
        accentColorInput.value = normalized
      }
    }
  }
}

// Validiere und normalisiere beim Verlassen des Feldes
const validateAndNormalizeColor = () => {
  if (!accentColorInput.value || accentColorInput.value.trim() === '') {
    accentColorInput.value = formData.value.accentColor
    return
  }
  
  const validation = validateHexColor(accentColorInput.value)
  if (!validation.valid) {
    accentColorError.value = validation.error || null
    // Setze zurück auf aktuellen Wert
    accentColorInput.value = formData.value.accentColor
    return
  }
  
  // Normalisiere und speichere
  const normalized = normalizeHexColor(accentColorInput.value)
  formData.value.accentColor = normalized
  accentColorInput.value = normalized
  accentColorError.value = null
}

// Reset Feature-Farbe
const resetAccentColor = () => {
  formData.value.accentColor = '#3B82F6'
  accentColorInput.value = '#3B82F6'
  accentColorError.value = null
}

const technologiesInput = computed({
  get: () => formData.value.technologies.join(', '),
  set: (value: string) => {
    formData.value.technologies = value.split(',').map(t => t.trim()).filter(t => t)
  }
})

// Verfügbare Projektseiten - automatisch aus Router-Routen
const availableProjectPages = computed(() => getProjectPageRoutes())

// Drag & Drop Handlers
const onReorderFavorites = async (newItems: PortfolioProject[]) => {
  const updates = newItems.map((p, index) => ({
    id: p.id!,
    favoriteOrder: index + 1
  }))
  try {
    await reorderProjects(updates)
  } catch (error) {
    handleError(error, 'Die Reihenfolge konnte nicht gespeichert werden.')
  }
}

const onReorderRegular = async (newItems: PortfolioProject[]) => {
  const updates = newItems.map((p, index) => ({
    id: p.id!,
    regularOrder: index + 1
  }))
  try {
    await reorderProjects(updates)
  } catch (error) {
    handleError(error, 'Die Reihenfolge konnte nicht gespeichert werden.')
  }
}

const favDnD = useDragAndDrop(favoriteProjects, onReorderFavorites)
const regDnD = useDragAndDrop(regularProjects, onReorderRegular)

const handleToggleFavorite = async (project: PortfolioProject) => {
  if (!project.id) return
  
  try {
    const isAutoProject = project.id.startsWith('auto-')
    const newStatus = !project.isFavorite
    
    // Wenn es ein auto-Projekt ist und als Favorit gesetzt werden soll, muss es zuerst in Firebase erstellt werden
    if (isAutoProject && newStatus) {
      // Erstelle das Projekt in Firebase mit den aktuellen Daten
      const projectData: Omit<PortfolioProject, 'id' | 'createdAt' | 'updatedAt'> = {
        title: project.title,
        description: project.description,
        screenshots: project.screenshots || [],
        technologies: project.technologies || [],
        projectPageUrl: project.projectPageUrl,
        logoLight: project.logoLight,
        logoDark: project.logoDark,
        accentColor: project.accentColor,
        gallery: project.gallery,
        isFavorite: true // Direkt als Favorit setzen
      }
      
      await createPortfolioProject(projectData)
    } else if (isAutoProject && !newStatus) {
      // Auto-Projekte können nicht von Favoriten entfernt werden, da sie nicht in Firebase existieren
      await modal.error('Automatische Projekte können nur als Favoriten gesetzt werden, wenn sie zuerst in Firebase erstellt wurden.')
      return
    } else {
      // Normales Firebase-Projekt: Toggle Favorit-Status
      await toggleFavoriteStatus(project.id, newStatus)
    }
    
    await loadProjects()
  } catch (error) {
    handleError(error, 'Status konnte nicht geändert werden.')
  }
}

const loadProjects = async () => {
  loading.value = true
  try {
    const firebaseProjects = await getPortfolioProjects()
    const { combinePortfolioProjects } = await import('../utils/portfolioHelper')
    const allProjects = await combinePortfolioProjects(firebaseProjects)
    
    // Favoriten und reguläre Projekte trennen und nach ihrer jeweiligen Order sortieren
    favoriteProjects.value = allProjects
      .filter(p => p.isFavorite)
      .sort((a, b) => (a.favoriteOrder || 0) - (b.favoriteOrder || 0))
      
    regularProjects.value = allProjects
      .filter(p => !p.isFavorite)
      .sort((a, b) => (a.regularOrder || 0) - (b.regularOrder || 0))
  } catch (error) {
    handleError(error, 'Fehler beim Laden der Projekte')
  } finally {
    loading.value = false
  }
}

// Automatische Erkennung der Feature-Farbe für bekannte Projekte
const detectFeatureColor = (projectPageUrl: string): string | null => {
  const knownProjects: Record<string, string> = {
    '/projects/m-drei': '#a50e34', // M-Drei rot
    // Weitere Projekte können hier hinzugefügt werden
  }
  return knownProjects[projectPageUrl] || null
}

const openCreateModal = () => {
  editingProject.value = null
  formData.value = {
    title: '',
    description: '',
    screenshots: [],
    technologies: [],
    liveUrl: '',
    githubUrl: '',
    projectPageUrl: '',
    logoLight: '',
    logoDark: '',
    accentColor: '#3B82F6',
    gallery: [],
    isFavorite: false
  }
  accentColorInput.value = '#3B82F6'
  accentColorError.value = null
  showModal.value = true
}

const openEditModal = (project: PortfolioProject) => {
  editingProject.value = project
  
  // Versuche Feature-Farbe automatisch zu erkennen, wenn nicht gesetzt
  let detectedColor = project.accentColor || '#3B82F6'
  if (!project.accentColor && project.projectPageUrl) {
    const autoDetected = detectFeatureColor(project.projectPageUrl)
    if (autoDetected) {
      detectedColor = autoDetected
    }
  }
  
  // Normalisiere die Farbe
  detectedColor = normalizeHexColor(detectedColor)
  
  formData.value = {
    title: project.title,
    description: project.description,
    screenshots: [...project.screenshots],
    technologies: [...project.technologies],
    liveUrl: project.liveUrl || '',
    githubUrl: project.githubUrl || '',
    projectPageUrl: project.projectPageUrl || '',
    logoLight: project.logoLight || '',
    logoDark: project.logoDark || '',
    accentColor: detectedColor,
    gallery: project.gallery ? [...project.gallery] : [],
    isFavorite: project.isFavorite || false
  }
  accentColorInput.value = detectedColor
  accentColorError.value = null
  showModal.value = true
}

const closeModal = () => {
  showModal.value = false
  editingProject.value = null
}

// ESC-Taste schließen für Create/Edit Modal
let escapeHandler: ((e: KeyboardEvent) => void) | null = null

watch(showModal, (newValue) => {
  if (newValue) {
    escapeHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeModal()
      }
    }
    document.addEventListener('keydown', escapeHandler)
  } else if (escapeHandler) {
    document.removeEventListener('keydown', escapeHandler)
    escapeHandler = null
  }
})

onUnmounted(() => {
  if (escapeHandler) {
    document.removeEventListener('keydown', escapeHandler)
  }
})

const handleFileUpload = async (event: Event) => {
  const target = event.target as HTMLInputElement
  const files = target.files

  if (!files || files.length === 0) return

  try {
    // Für neue Projekte verwenden wir eine temporäre ID
    const projectId = editingProject.value?.id || 'temp-' + Date.now()
    const uploadPromises: Promise<string>[] = []

    for (let i = 0; i < files.length; i++) {
      uploadPromises.push(uploadScreenshot(files[i], projectId, formData.value.screenshots.length + i))
    }

    const urls = await Promise.all(uploadPromises)
    formData.value.screenshots.push(...urls)
  } catch (error) {
    handleError(error, 'Fehler beim Hochladen der Screenshots')
  } finally {
    if (fileInput.value) {
      fileInput.value.value = ''
    }
  }
}

const removeScreenshot = (index: number) => {
  formData.value.screenshots.splice(index, 1)
}

// Handler für Projektseiten-Änderung - erkennt automatisch Feature-Farbe
const handleProjectPageChange = () => {
  if (formData.value.projectPageUrl) {
    const detectedColor = detectFeatureColor(formData.value.projectPageUrl)
    if (detectedColor && (!formData.value.accentColor || formData.value.accentColor === '#3B82F6')) {
      const normalized = normalizeHexColor(detectedColor)
      formData.value.accentColor = normalized
      accentColorInput.value = normalized
      accentColorError.value = null
    }
  }
}

const handleSubmit = async () => {
  submitting.value = true
  try {
    // Validiere und normalisiere die Feature-Farbe vor dem Absenden
    if (accentColorInput.value && accentColorInput.value.trim() !== '') {
      const validation = validateHexColor(accentColorInput.value)
      if (validation.valid) {
        // Normalisiere und speichere
        const normalized = normalizeHexColor(accentColorInput.value)
        formData.value.accentColor = normalized
        accentColorInput.value = normalized
        accentColorError.value = null
      } else {
        // Wenn ungültig, zeige Fehler und verhindere Absenden
        accentColorError.value = validation.error || null
        submitting.value = false
        await modal.error(validation.error || 'Bitte geben Sie einen gültigen HEX-Code ein.', 'Ungültige Farbe')
        return
      }
    }
    
    const projectData: Omit<PortfolioProject, 'id' | 'createdAt' | 'updatedAt'> = {
      title: formData.value.title,
      description: formData.value.description,
      screenshots: formData.value.screenshots,
      technologies: formData.value.technologies,
      logoLight: formData.value.logoLight,
      logoDark: formData.value.logoDark,
      accentColor: formData.value.accentColor,
      gallery: formData.value.gallery,
      isFavorite: formData.value.isFavorite
    }
    
    // Füge optionale Felder nur hinzu, wenn sie nicht leer sind
    if (formData.value.liveUrl && formData.value.liveUrl.trim()) {
      projectData.liveUrl = formData.value.liveUrl.trim()
    }
    if (formData.value.githubUrl && formData.value.githubUrl.trim()) {
      projectData.githubUrl = formData.value.githubUrl.trim()
    }
    if (formData.value.projectPageUrl && formData.value.projectPageUrl.trim()) {
      projectData.projectPageUrl = formData.value.projectPageUrl.trim()
    }

    // Prüfe, ob es ein automatisches Projekt ist (ID beginnt mit "auto-")
    const isAutoProject = editingProject.value?.id?.startsWith('auto-')
    
    if (isAutoProject) {
      // Automatisches Projekt: Erstelle neues Firebase-Projekt
      await createPortfolioProject(projectData)
    } else if (editingProject.value?.id) {
      // Bestehendes Firebase-Projekt: Aktualisiere
      await updatePortfolioProject(editingProject.value.id, projectData)
    } else {
      // Neues Projekt: Erstelle in Firebase
      await createPortfolioProject(projectData)
    }

    closeModal()
    await loadProjects()
  } catch (error) {
    handleError(error, 'Fehler beim Speichern des Projekts')
  } finally {
    submitting.value = false
  }
}

const handleDelete = async (id: string, screenshots: string[], logoLight?: string, logoDark?: string, gallery?: { url: string }[]) => {
  // Automatische Projekte können nicht gelöscht werden (sie existieren nur als Platzhalter)
  if (id.startsWith('auto-')) {
    await modal.error('Automatische Projekte können nicht gelöscht werden. Sie werden automatisch erstellt, wenn eine Projektseite existiert.')
    return
  }
  
  const confirmed = await modal.confirm('Möchten Sie dieses Projekt wirklich löschen?', 'Projekt löschen')
  if (!confirmed) return

  try {
    const galleryUrls = gallery?.map(g => g.url) || []
    await deletePortfolioProject(id, screenshots, logoLight, logoDark, galleryUrls)
    await loadProjects()
    await modal.success('Projekt erfolgreich gelöscht!')
  } catch (error) {
    handleError(error, 'Fehler beim Löschen des Projekts')
  }
}

const handleLogout = async () => {
  try {
    await logout()
    router.push('/login')
  } catch (error) {
    handleError(error, 'Fehler beim Abmelden')
  }
}

// Bilder Tab Funktionen
const loadImages = async () => {
  loadingImages.value = true
  try {
    siteImages.value = await getAllImages()
  } catch (error) {
    handleError(error, 'Fehler beim Laden der Bilder')
  } finally {
    loadingImages.value = false
  }
}

const handleImageUpload = async (event: Event, category: ImageCategory) => {
  const target = event.target as HTMLInputElement
  const file = target.files?.[0]
  
  if (!file) return
  
  // Validiere Bildname
  const imageName = category === 'home'
    ? homeImageName.value.trim()
    : category === 'about'
      ? aboutImageName.value.trim()
      : heroWebsiteImageName.value.trim()
  
  // Wenn kein Name angegeben, verwende den Dateinamen (ohne Extension)
  const finalImageName = imageName || file.name.replace(/\.[^/.]+$/, '')
  
  // Validiere den finalen Namen
  const validation = validateImageName(finalImageName)
  if (!validation.valid) {
    if (category === 'home') {
      homeImageNameError.value = validation.error || 'Ungültiger Bildname'
    } else if (category === 'about') {
      aboutImageNameError.value = validation.error || 'Ungültiger Bildname'
    } else if (category === 'hero-website') {
      heroWebsiteImageNameError.value = validation.error || 'Ungültiger Bildname'
    }
    await modal.error(validation.error || 'Bitte geben Sie einen gültigen Bildnamen ein.', 'Ungültiger Bildname')
    return
  }
  
  if (category === 'home') {
    uploadingHome.value = true
    homeImageNameError.value = null
  } else if (category === 'about') {
    uploadingAbout.value = true
    aboutImageNameError.value = null
  } else if (category === 'hero-website') {
    uploadingHeroWebsite.value = true
    heroWebsiteImageNameError.value = null
  }
  
  try {
    await uploadImage(
      file,
      category,
      finalImageName,
      category === 'hero-website'
        ? {
            displayTitle: heroWebsiteDisplayTitle.value,
            linkUrl: heroWebsiteLinkUrl.value
          }
        : undefined
    )
    await loadImages()
    
    // Reset
    if (category === 'home') {
      homeImageName.value = ''
      homeImageNameError.value = null
      if (homeImageInput.value) homeImageInput.value.value = ''
    } else if (category === 'about') {
      aboutImageName.value = ''
      aboutImageNameError.value = null
      if (aboutImageInput.value) aboutImageInput.value.value = ''
    } else if (category === 'hero-website') {
      heroWebsiteImageName.value = ''
      heroWebsiteDisplayTitle.value = ''
      heroWebsiteLinkUrl.value = ''
      heroWebsiteImageNameError.value = null
      if (heroWebsiteImageInput.value) heroWebsiteImageInput.value.value = ''
    }
    
    await modal.success('Bild erfolgreich hochgeladen!')
  } catch (error) {
    handleError(error, 'Fehler beim Hochladen des Bildes')
  } finally {
    if (category === 'home') {
      uploadingHome.value = false
    } else if (category === 'about') {
      uploadingAbout.value = false
    } else if (category === 'hero-website') {
      uploadingHeroWebsite.value = false
    }
  }
}

const handleBatchConvertImages = async () => {
  const confirmed = await modal.confirm(
    'Möchten Sie wirklich alle Bilder zu WebP konvertieren? Dies kann einige Zeit dauern. Bereits WebP-Bilder werden übersprungen.',
    'Bilder zu WebP konvertieren'
  )
  
  if (!confirmed) return
  
  convertingImages.value = true
  convertProgress.value = { current: 0, total: 0, imageName: '' }
  convertResult.value = null
  
  try {
    const result = await batchConvertImagesToWebP((current, total, imageName) => {
      convertProgress.value = { current, total, imageName }
    })
    
    convertResult.value = result
    
    // Lade Bilder neu, um die aktualisierten URLs zu sehen
    await loadImages()
    
    const totalSavings = Math.round((1 - result.totalSizeAfter / result.totalSizeBefore) * 100)
    await modal.success(
      `Konvertierung abgeschlossen!\n\n` +
      `Konvertiert: ${result.converted}\n` +
      `Übersprungen: ${result.skipped}\n` +
      `Fehlgeschlagen: ${result.failed}\n\n` +
      `Gesamteinsparung: ${(result.totalSizeBefore / 1024 / 1024).toFixed(2)} MB → ${(result.totalSizeAfter / 1024 / 1024).toFixed(2)} MB (${totalSavings}%)`,
      'Batch-Konvertierung erfolgreich'
    )
  } catch (error) {
    handleError(error, 'Fehler bei Batch-Konvertierung')
  } finally {
    convertingImages.value = false
  }
}

const handleDeleteImage = async (image: SiteImage) => {
  const confirmed = await modal.confirm(
    `Möchten Sie das Bild "${image.name}" wirklich löschen?`,
    'Bild löschen'
  )
  if (!confirmed) return
  
  try {
    await deleteImage(image.id, image.storagePath)
    await loadImages()
    await modal.success('Bild erfolgreich gelöscht!')
  } catch (error) {
    handleError(error, 'Fehler beim Löschen des Bildes')
  }
}


const startEditSiteImageName = (imageId: string, currentName: string) => {
  editingSiteImageName.value = { id: imageId, name: currentName }
}

const saveSiteImageName = async (imageId: string) => {
  if (!editingSiteImageName.value || editingSiteImageName.value.id !== imageId) return
  
  const newName = editingSiteImageName.value.name.trim()
  if (!newName) {
    await modal.error('Der Bildname darf nicht leer sein', 'Ungültiger Bildname')
    cancelEditSiteImageName()
    return
  }
  
  try {
    await updateImageName(imageId, newName)
    await loadImages()
    editingSiteImageName.value = null
  } catch (error) {
    handleError(error, 'Fehler beim Aktualisieren des Bildnamens')
    cancelEditSiteImageName()
  }
}

const cancelEditSiteImageName = () => {
  editingSiteImageName.value = null
}

const saveHeroWebsiteImageDetails = async (
  image: SiteImage,
  field: 'displayTitle' | 'linkUrl',
  event: Event
) => {
  const value = (event.target as HTMLInputElement).value.trim()
  const currentValue = (image[field] || '').trim()

  if (value === currentValue) return

  try {
    await updateImageDetails(image.id, {
      displayTitle: field === 'displayTitle' ? value : image.displayTitle,
      linkUrl: field === 'linkUrl' ? value : image.linkUrl
    })
    await loadImages()
  } catch (error) {
    handleError(error, 'Fehler beim Speichern der Hero-Bilddaten')
  }
}

const homeImages = computed(() => siteImages.value.filter(img => img.category === 'home'))
const aboutImages = computed(() => siteImages.value.filter(img => img.category === 'about'))
const heroWebsiteImages = computed(() => siteImages.value.filter(img => img.category === 'hero-website'))

// Verfügbare Bildnamen für Vorschläge
const existingHomeImageNames = computed(() => {
  const names = new Set(homeImages.value.map(img => img.name))
  return Array.from(names).sort()
})

const existingAboutImageNames = computed(() => {
  const names = new Set(aboutImages.value.map(img => img.name))
  return Array.from(names).sort()
})

const existingHeroWebsiteImageNames = computed(() => {
  const names = new Set(heroWebsiteImages.value.map(img => img.name))
  return Array.from(names).sort()
})

// Vorschläge für Bildnamen
const homeImageNameSuggestions = computed(() => {
  if (!homeImageName.value || homeImageName.value.trim() === '') {
    return existingHomeImageNames.value.slice(0, 5)
  }
  const search = homeImageName.value.toLowerCase()
  return existingHomeImageNames.value
    .filter(name => name.toLowerCase().includes(search))
    .slice(0, 5)
})

const aboutImageNameSuggestions = computed(() => {
  if (!aboutImageName.value || aboutImageName.value.trim() === '') {
    return existingAboutImageNames.value.slice(0, 5)
  }
  const search = aboutImageName.value.toLowerCase()
  return existingAboutImageNames.value
    .filter(name => name.toLowerCase().includes(search))
    .slice(0, 5)
})

const heroWebsiteImageNameSuggestions = computed(() => {
  if (!heroWebsiteImageName.value || heroWebsiteImageName.value.trim() === '') {
    return existingHeroWebsiteImageNames.value.slice(0, 5)
  }
  const search = heroWebsiteImageName.value.toLowerCase()
  return existingHeroWebsiteImageNames.value
    .filter(name => name.toLowerCase().includes(search))
    .slice(0, 5)
})

// Dropdown States
const showHomeSuggestions = ref(false)
const showAboutSuggestions = ref(false)
const showHeroWebsiteSuggestions = ref(false)

// Validierung
const validateImageName = (name: string): { valid: boolean; error?: string } => {
  if (!name || name.trim() === '') {
    return { valid: false, error: 'Bildname ist erforderlich' }
  }
  if (name.length < 2) {
    return { valid: false, error: 'Bildname muss mindestens 2 Zeichen lang sein' }
  }
  if (name.length > 50) {
    return { valid: false, error: 'Bildname darf maximal 50 Zeichen lang sein' }
  }
  // Erlaube nur alphanumerische Zeichen, Bindestriche und Unterstriche
  if (!/^[a-zA-Z0-9_-]+$/.test(name)) {
    return { valid: false, error: 'Bildname darf nur Buchstaben, Zahlen, Bindestriche und Unterstriche enthalten' }
  }
  return { valid: true }
}

const homeImageNameError = ref<string | null>(null)
const aboutImageNameError = ref<string | null>(null)
const heroWebsiteImageNameError = ref<string | null>(null)

const validateHomeImageName = () => {
  const validation = validateImageName(homeImageName.value)
  homeImageNameError.value = validation.valid ? null : validation.error || null
}

const validateAboutImageName = () => {
  const validation = validateImageName(aboutImageName.value)
  aboutImageNameError.value = validation.valid ? null : validation.error || null
}

const validateHeroWebsiteImageName = () => {
  const validation = validateImageName(heroWebsiteImageName.value)
  heroWebsiteImageNameError.value = validation.valid ? null : validation.error || null
}

const selectHomeImageName = (name: string) => {
  homeImageName.value = name
  showHomeSuggestions.value = false
  validateHomeImageName()
}

const selectAboutImageName = (name: string) => {
  aboutImageName.value = name
  showAboutSuggestions.value = false
  validateAboutImageName()
}

const selectHeroWebsiteImageName = (name: string) => {
  heroWebsiteImageName.value = name
  showHeroWebsiteSuggestions.value = false
  validateHeroWebsiteImageName()
}

const hideHomeSuggestions = () => {
  setTimeout(() => {
    showHomeSuggestions.value = false
  }, 200)
}

const hideAboutSuggestions = () => {
  setTimeout(() => {
    showAboutSuggestions.value = false
  }, 200)
}

const hideHeroWebsiteSuggestions = () => {
  setTimeout(() => {
    showHeroWebsiteSuggestions.value = false
  }, 200)
}

// Favicon-Funktionen
const loadFavicon = async () => {
  loadingFavicon.value = true
  try {
    currentFaviconUrl.value = await getFaviconUrl()
  } catch (error) {
    logger.error('Fehler beim Laden des Favicons', error)
  } finally {
    loadingFavicon.value = false
  }
}

const handleFaviconUpload = async (event: Event) => {
  const target = event.target as HTMLInputElement
  const file = target.files?.[0]
  
  if (!file) return
  
  // Validiere Dateityp
  const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/x-icon', 'image/vnd.microsoft.icon', 'image/webp']
  const fileExtension = file.name.split('.').pop()?.toLowerCase()
  const validExtensions = ['png', 'jpg', 'jpeg', 'svg', 'ico', 'webp']
  
  if (!validTypes.includes(file.type) && !validExtensions.includes(fileExtension || '')) {
    await modal.error('Ungültiges Dateiformat. Bitte verwenden Sie ICO, PNG, SVG, JPG oder WEBP.', 'Ungültiges Format')
    if (faviconInput.value) faviconInput.value.value = ''
    return
  }
  
  uploadingFavicon.value = true
  
  try {
    const url = await uploadFavicon(file)
    currentFaviconUrl.value = url
    
    // Aktualisiere das Favicon sofort im Browser
    updateFaviconInHead(url)
    
    await modal.success('Favicon erfolgreich hochgeladen und angewendet!')
  } catch (error) {
    handleError(error, 'Fehler beim Hochladen des Favicons')
  } finally {
    uploadingFavicon.value = false
    if (faviconInput.value) faviconInput.value.value = ''
  }
}

const handleDeleteFavicon = async () => {
  const confirmed = await modal.confirm(
    'Möchten Sie das Favicon wirklich löschen? Das Standard-Favicon wird dann verwendet.',
    'Favicon löschen'
  )
  if (!confirmed) return
  
  try {
    await deleteFavicon()
    currentFaviconUrl.value = null
    
    // Setze das Standard-Favicon zurück
    resetFaviconToDefault()
    
    await modal.success('Favicon erfolgreich gelöscht!')
  } catch (error) {
    handleError(error, 'Fehler beim Löschen des Favicons')
  }
}

const updateFaviconInHead = (url: string) => {
  // Entferne vorhandene Favicon-Links
  const existingLinks = document.querySelectorAll('link[rel="icon"], link[rel="shortcut icon"]')
  existingLinks.forEach(link => link.remove())

  // Bestimme den MIME-Type
  const extension = url.split('.').pop()?.toLowerCase()
  let type = 'image/x-icon'
  
  if (extension === 'svg') {
    type = 'image/svg+xml'
  } else if (extension === 'png') {
    type = 'image/png'
  } else if (extension === 'webp') {
    type = 'image/webp'
  } else if (extension === 'jpg' || extension === 'jpeg') {
    type = 'image/jpeg'
  }

  // Erstelle neuen Favicon-Link
  const link = document.createElement('link')
  link.rel = 'icon'
  link.type = type
  link.href = url
  document.head.appendChild(link)

  // Füge auch shortcut icon für ältere Browser hinzu
  const shortcutLink = document.createElement('link')
  shortcutLink.rel = 'shortcut icon'
  shortcutLink.type = type
  shortcutLink.href = url
  document.head.appendChild(shortcutLink)
}

const resetFaviconToDefault = () => {
  // Entferne vorhandene Favicon-Links
  const existingLinks = document.querySelectorAll('link[rel="icon"], link[rel="shortcut icon"]')
  existingLinks.forEach(link => link.remove())

  // Setze das Standard-Favicon zurück (aus index.html)
  const defaultLink = document.createElement('link')
  defaultLink.rel = 'icon'
  defaultLink.type = 'image/svg+xml'
  defaultLink.href = '/vite.svg'
  document.head.appendChild(defaultLink)
}

// Share-Bild-Funktionen
const loadShareImage = async () => {
  loadingShareImage.value = true
  try {
    currentShareImageUrl.value = await getShareImageUrl()
  } catch (error) {
    logger.error('Fehler beim Laden des Share-Bildes', error)
  } finally {
    loadingShareImage.value = false
  }
}

// Kontaktformular-Submissions Funktionen
const loadSubmissions = async () => {
  loadingSubmissions.value = true
  try {
    submissions.value = await getAllContactSubmissions()
  } catch (error) {
    handleError(error, 'Fehler beim Laden der Submissions')
  } finally {
    loadingSubmissions.value = false
  }
}

const filteredSubmissions = computed(() => {
  let filtered = [...submissions.value]

  // Filter nach Status
  if (submissionFilter.value === 'spam') {
    filtered = filtered.filter(s => s.isSpam)
  } else if (submissionFilter.value === 'not-spam') {
    filtered = filtered.filter(s => !s.isSpam)
  } else if (submissionFilter.value === 'unread') {
    filtered = filtered.filter(s => !s.isRead)
  } else if (submissionFilter.value === 'processed') {
    filtered = filtered.filter(s => s.isProcessed)
  }

  // Suche
  if (submissionSearch.value.trim()) {
    const searchLower = submissionSearch.value.toLowerCase()
    filtered = filtered.filter(s => 
      s.name.toLowerCase().includes(searchLower) ||
      s.email.toLowerCase().includes(searchLower) ||
      s.message.toLowerCase().includes(searchLower) ||
      (s.interest && getInterestLabel(s.interest).toLowerCase().includes(searchLower))
    )
  }

  return filtered
})

const allSelected = computed(() => {
  return filteredSubmissions.value.length > 0 && 
         filteredSubmissions.value.every(s => selectedSubmissions.value.includes(s.id))
})

const toggleSelection = (submissionId: string) => {
  const index = selectedSubmissions.value.indexOf(submissionId)
  if (index > -1) {
    selectedSubmissions.value.splice(index, 1)
  } else {
    selectedSubmissions.value.push(submissionId)
  }
}

const toggleSelectAll = () => {
  if (allSelected.value) {
    // Alle deselektieren
    filteredSubmissions.value.forEach(s => {
      const index = selectedSubmissions.value.indexOf(s.id)
      if (index > -1) {
        selectedSubmissions.value.splice(index, 1)
      }
    })
  } else {
    // Alle auswählen
    filteredSubmissions.value.forEach(s => {
      if (!selectedSubmissions.value.includes(s.id)) {
        selectedSubmissions.value.push(s.id)
      }
    })
  }
}

const formatDate = (date: Date) => {
  return new Intl.DateTimeFormat('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date)
}

const getInterestLabel = (value: string): string => {
  const labels: Record<string, string> = {
    'wordpress': 'WordPress Development',
    'webdev': 'Webentwicklung (Vue, React, TypeScript)',
    'custom-tools': 'Custom Tools & Lösungen',
    'consulting': 'Allgemeine Beratung',
    'other': 'Sonstiges'
  }
  return labels[value] || value
}

const getProjectTypeLabel = (value: string): string => {
  const labels: Record<string, string> = {
    'new': 'Neues Projekt starten',
    'extend': 'Bestehendes Projekt erweitern',
    'support': 'Fehlerbehebung / Support',
    'consulting': 'Beratung / Konzept',
    'other': 'Sonstiges'
  }
  return labels[value] || value
}

const getBudgetLabel = (value: string): string => {
  const labels: Record<string, string> = {
    'under-1k': 'Unter 1.000 €',
    '1k-5k': '1.000 € - 5.000 €',
    '5k-10k': '5.000 € - 10.000 €',
    '10k-25k': '10.000 € - 25.000 €',
    'over-25k': 'Über 25.000 €',
    'unclear': 'Noch unklar'
  }
  return labels[value] || value
}

const copySubmissionEmail = async (email: string) => {
  try {
    await navigator.clipboard.writeText(email)
    await modal.success('E-Mail-Adresse wurde in die Zwischenablage kopiert', 'Kopiert')
  } catch (error) {
    handleError(error, 'Fehler beim Kopieren')
  }
}

const markAsSpam = async (submissionId: string) => {
  try {
    const confirmed = await modal.confirm(
      'Möchten Sie diese Submission wirklich als Spam markieren?',
      'Als Spam markieren'
    )
    if (!confirmed) return

    await markSubmissionAsSpam(submissionId)
    await loadSubmissions()
    await modal.success('Submission wurde als Spam markiert', 'Erfolg')
  } catch (error) {
    handleError(error, 'Fehler beim Markieren als Spam')
  }
}

const deleteSubmission = async (submissionId: string) => {
  try {
    const confirmed = await modal.confirm(
      'Möchten Sie diese Submission wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.',
      'Submission löschen'
    )
    if (!confirmed) return

    await deleteContactSubmission(submissionId)
    selectedSubmissions.value = selectedSubmissions.value.filter(id => id !== submissionId)
    await loadSubmissions()
    await modal.success('Submission wurde gelöscht', 'Erfolg')
  } catch (error) {
    handleError(error, 'Fehler beim Löschen')
  }
}

const markAsProcessed = async (submissionId: string) => {
  try {
    await markSubmissionAsProcessed(submissionId)
    await loadSubmissions()
    await modal.success('Submission wurde als bearbeitet markiert', 'Erfolg')
  } catch (error) {
    handleError(error, 'Fehler beim Markieren als bearbeitet')
  }
}

const markAsUnread = async (submissionId: string) => {
  try {
    await markSubmissionAsUnread(submissionId)
    await loadSubmissions()
    await modal.success('Submission wurde als ungelesen markiert', 'Erfolg')
  } catch (error) {
    handleError(error, 'Fehler beim Markieren als ungelesen')
  }
}

const bulkMarkAsProcessed = async () => {
  if (selectedSubmissions.value.length === 0) return
  
  try {
    const count = selectedSubmissions.value.length
    const confirmed = await modal.confirm(
      `Möchten Sie ${count} Submission(s) wirklich als bearbeitet markieren?`,
      'Als bearbeitet markieren'
    )
    if (!confirmed) return

    processingBulk.value = true
    await markSubmissionsAsProcessed(selectedSubmissions.value)
    selectedSubmissions.value = []
    await loadSubmissions()
    await modal.success(`${count} Submission(s) wurden als bearbeitet markiert`, 'Erfolg')
  } catch (error) {
    handleError(error, 'Fehler beim Markieren als bearbeitet')
  } finally {
    processingBulk.value = false
  }
}

const bulkDelete = async () => {
  if (selectedSubmissions.value.length === 0) return
  
  try {
    const confirmed = await modal.confirm(
      `Möchten Sie wirklich ${selectedSubmissions.value.length} Submission(s) löschen? Diese Aktion kann nicht rückgängig gemacht werden.`,
      'Submissions löschen'
    )
    if (!confirmed) return

    processingBulk.value = true
    await deleteContactSubmissions(selectedSubmissions.value)
    const count = selectedSubmissions.value.length
    selectedSubmissions.value = []
    await loadSubmissions()
    await modal.success(`${count} Submission(s) wurden gelöscht`, 'Erfolg')
  } catch (error) {
    handleError(error, 'Fehler beim Löschen')
  } finally {
    processingBulk.value = false
  }
}

const deleteAllSubmissions = async () => {
  try {
    const confirmed = await modal.confirm(
      `Möchten Sie wirklich ALLE ${submissions.value.length} Submissions löschen? Diese Aktion kann nicht rückgängig gemacht werden!`,
      'Alle Submissions löschen'
    )
    if (!confirmed) return

    processingBulk.value = true
    const count = await deleteAllContactSubmissions()
    selectedSubmissions.value = []
    await loadSubmissions()
    await modal.success(`${count} Submission(s) wurden gelöscht`, 'Erfolg')
  } catch (error) {
    handleError(error, 'Fehler beim Löschen aller Submissions')
  } finally {
    processingBulk.value = false
  }
}

/**
 * Verarbeitet ein Bild und passt es für Social Media Sharing an
 * - Skaliert auf 1200x630 Pixel (Open Graph Standard)
 * - Fügt weißen Hintergrund bei Transparenz hinzu
 * - Zentriert das Bild mit Padding
 */
const processShareImage = async (file: File): Promise<File> => {
  return new Promise((resolve, reject) => {
    const img = new Image()
    const reader = new FileReader()
    
    reader.onload = (e) => {
      img.src = e.target?.result as string
    }
    
    reader.onerror = () => {
      reject(new Error('Fehler beim Lesen der Bilddatei'))
    }
    
    img.onload = () => {
      try {
        // Ziel-Dimensionen für Open Graph
        const targetWidth = 1200
        const targetHeight = 630
        
        // Canvas erstellen
        const canvas = document.createElement('canvas')
        canvas.width = targetWidth
        canvas.height = targetHeight
        const ctx = canvas.getContext('2d')
        
        if (!ctx) {
          reject(new Error('Canvas-Kontext konnte nicht erstellt werden'))
          return
        }
        
        // Weißen Hintergrund zeichnen
        ctx.fillStyle = '#FFFFFF'
        ctx.fillRect(0, 0, targetWidth, targetHeight)
        
        // Berechne Skalierung und Position (mit Padding)
        const padding = 40
        const maxWidth = targetWidth - (padding * 2)
        const maxHeight = targetHeight - (padding * 2)
        
        let scale = Math.min(maxWidth / img.width, maxHeight / img.height)
        
        // Begrenze die Skalierung, um zu kleine Bilder nicht zu stark zu vergrößern
        if (scale > 1.5) {
          scale = 1.5
        }
        
        const scaledWidth = img.width * scale
        const scaledHeight = img.height * scale
        
        // Zentriere das Bild
        const x = (targetWidth - scaledWidth) / 2
        const y = (targetHeight - scaledHeight) / 2
        
        // Zeichne das Bild
        ctx.drawImage(img, x, y, scaledWidth, scaledHeight)
        
        // Konvertiere Canvas zu Blob
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Fehler beim Erstellen des Bildes'))
              return
            }
            
            // Erstelle neue Datei mit korrektem Namen (WebP für bessere Kompression)
            const processedFile = new File(
              [blob],
              file.name.replace(/\.[^/.]+$/, '') + '-og.webp',
              { type: 'image/webp' }
            )
            
            resolve(processedFile)
          },
          'image/webp',
          0.85 // Qualität (WebP ist effizienter als JPEG)
        )
      } catch (error) {
        reject(error)
      }
    }
    
    img.onerror = () => {
      reject(new Error('Fehler beim Laden des Bildes'))
    }
    
    reader.readAsDataURL(file)
  })
}

/**
 * Validiert die Dateigröße des Bildes
 */
const validateFileSize = (file: File): boolean => {
  const maxSize = 8 * 1024 * 1024 // 8 MB
  return file.size <= maxSize
}

const handleShareImageUpload = async (event: Event) => {
  const target = event.target as HTMLInputElement
  const file = target.files?.[0]
  
  if (!file) return
  
  // Validiere Dateityp
  const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp']
  const fileExtension = file.name.split('.').pop()?.toLowerCase()
  const validExtensions = ['png', 'jpg', 'jpeg', 'webp']
  
  if (!validTypes.includes(file.type) && !validExtensions.includes(fileExtension || '')) {
    await modal.error('Ungültiges Dateiformat. Bitte verwenden Sie PNG, JPG oder WEBP.', 'Ungültiges Format')
    if (shareImageInput.value) shareImageInput.value.value = ''
    return
  }
  
  // Validiere Dateigröße
  if (!validateFileSize(file)) {
    await modal.error('Die Datei ist zu groß. Maximale Größe: 8 MB', 'Datei zu groß')
    if (shareImageInput.value) shareImageInput.value.value = ''
    return
  }
  
  uploadingShareImage.value = true
  
  try {
    // Verarbeite das Bild (1200x630, weißer Hintergrund)
    const processedFile = await processShareImage(file)
    
    // Lade das verarbeitete Bild hoch
    const url = await uploadShareImage(processedFile)
    currentShareImageUrl.value = url
    
    // Aktualisiere die Meta-Tags sofort
    updateShareImageMetaTags(url)
    
    await modal.success('Share-Bild erfolgreich hochgeladen und auf 1200x630 Pixel angepasst!')
  } catch (error) {
    handleError(error, 'Fehler beim Hochladen des Share-Bildes')
  } finally {
    uploadingShareImage.value = false
    if (shareImageInput.value) shareImageInput.value.value = ''
  }
}

const handleDeleteShareImage = async () => {
  const confirmed = await modal.confirm(
    'Möchten Sie das Share-Bild wirklich löschen? Das Standard-Bild wird dann verwendet.',
    'Share-Bild löschen'
  )
  if (!confirmed) return
  
  try {
    await deleteShareImage()
    currentShareImageUrl.value = null
    
    // Setze die Standard-Meta-Tags zurück (inkl. Dimensionen)
    await resetShareImageMetaTags()
    
    await modal.success('Share-Bild erfolgreich gelöscht!')
  } catch (error) {
    handleError(error, 'Fehler beim Löschen des Share-Bildes')
  }
}

const getImageDimensions = (url: string): Promise<{ width: number; height: number }> => {
  return new Promise((resolve) => {
    const img = new Image()
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight })
    }
    img.onerror = () => {
      // Fallback: Verwende Standard-Dimensionen für Share-Bilder (1200x630)
      resolve({ width: 1200, height: 630 })
    }
    img.src = url
  })
}

const updateShareImageMetaTags = async (url: string) => {
  // Stelle sicher, dass die URL absolut ist
  const absoluteUrl = url.startsWith('http://') || url.startsWith('https://')
    ? url
    : (url.startsWith('/')
      ? `${window.location.protocol}//${window.location.host}${url}`
      : `${window.location.protocol}//${window.location.host}/${url}`)
  
  // Aktualisiere Open Graph Image
  let ogImage = document.querySelector('meta[property="og:image"]')
  if (!ogImage) {
    ogImage = document.createElement('meta')
    ogImage.setAttribute('property', 'og:image')
    document.head.appendChild(ogImage)
  }
  ogImage.setAttribute('content', absoluteUrl)

  // Aktualisiere Twitter Image
  let twitterImage = document.querySelector('meta[property="twitter:image"]')
  if (!twitterImage) {
    twitterImage = document.createElement('meta')
    twitterImage.setAttribute('property', 'twitter:image')
    document.head.appendChild(twitterImage)
  }
  twitterImage.setAttribute('content', absoluteUrl)

  // Twitter Card Type
  let twitterCard = document.querySelector('meta[property="twitter:card"]')
  if (!twitterCard) {
    twitterCard = document.createElement('meta')
    twitterCard.setAttribute('property', 'twitter:card')
    document.head.appendChild(twitterCard)
  }
  twitterCard.setAttribute('content', 'summary_large_image')

  // Da wir das Bild immer auf 1200x630 verarbeiten, setze diese Dimensionen
  const ogWidth = 1200
  const ogHeight = 630
  
  // Setze og:image:width
  let ogImageWidth = document.querySelector('meta[property="og:image:width"]')
  if (!ogImageWidth) {
    ogImageWidth = document.createElement('meta')
    ogImageWidth.setAttribute('property', 'og:image:width')
    document.head.appendChild(ogImageWidth)
  }
  ogImageWidth.setAttribute('content', ogWidth.toString())

  // Setze og:image:height
  let ogImageHeight = document.querySelector('meta[property="og:image:height"]')
  if (!ogImageHeight) {
    ogImageHeight = document.createElement('meta')
    ogImageHeight.setAttribute('property', 'og:image:height')
    document.head.appendChild(ogImageHeight)
  }
  ogImageHeight.setAttribute('content', ogHeight.toString())

  // Setze og:image:type
  let ogImageType = document.querySelector('meta[property="og:image:type"]')
  if (!ogImageType) {
    ogImageType = document.createElement('meta')
    ogImageType.setAttribute('property', 'og:image:type')
    document.head.appendChild(ogImageType)
  }
  ogImageType.setAttribute('content', 'image/jpeg')
  
  // Setze og:image:alt
  let ogImageAlt = document.querySelector('meta[property="og:image:alt"]')
  if (!ogImageAlt) {
    ogImageAlt = document.createElement('meta')
    ogImageAlt.setAttribute('property', 'og:image:alt')
    document.head.appendChild(ogImageAlt)
  }
  ogImageAlt.setAttribute('content', 'Maximilian Reitsberger - Portfolio')
}

const resetShareImageMetaTags = async () => {
  // Setze die Standard-URLs zurück (aus index.html) - verwende absolute URL
  const defaultImage = APP_CONFIG.getAbsoluteUrl('/images/SharePicture.png')
  
  let ogImage = document.querySelector('meta[property="og:image"]')
  if (ogImage) {
    ogImage.setAttribute('content', defaultImage)
  }

  let twitterImage = document.querySelector('meta[property="twitter:image"]')
  if (twitterImage) {
    twitterImage.setAttribute('content', defaultImage)
  }

  // Setze auch die Dimensionen für das Standard-Bild
  try {
    const dimensions = await getImageDimensions(defaultImage)
    
    let ogImageWidth = document.querySelector('meta[property="og:image:width"]')
    if (!ogImageWidth) {
      ogImageWidth = document.createElement('meta')
      ogImageWidth.setAttribute('property', 'og:image:width')
      document.head.appendChild(ogImageWidth)
    }
    ogImageWidth.setAttribute('content', dimensions.width.toString())

    let ogImageHeight = document.querySelector('meta[property="og:image:height"]')
    if (!ogImageHeight) {
      ogImageHeight = document.createElement('meta')
      ogImageHeight.setAttribute('property', 'og:image:height')
      document.head.appendChild(ogImageHeight)
    }
    ogImageHeight.setAttribute('content', dimensions.height.toString())
  } catch (error) {
    logger.error('Fehler beim Laden der Standard-Bilddimensionen', error)
    // Setze Standard-Dimensionen als Fallback
    const defaultDimensions = { width: 1200, height: 630 }
    
    let ogImageWidth = document.querySelector('meta[property="og:image:width"]')
    if (!ogImageWidth) {
      ogImageWidth = document.createElement('meta')
      ogImageWidth.setAttribute('property', 'og:image:width')
      document.head.appendChild(ogImageWidth)
    }
    ogImageWidth.setAttribute('content', defaultDimensions.width.toString())

    let ogImageHeight = document.querySelector('meta[property="og:image:height"]')
    if (!ogImageHeight) {
      ogImageHeight = document.createElement('meta')
      ogImageHeight.setAttribute('property', 'og:image:height')
      document.head.appendChild(ogImageHeight)
    }
    ogImageHeight.setAttribute('content', defaultDimensions.height.toString())
  }
}

onMounted(async () => {
  // Prüfe, ob der Benutzer authentifiziert ist
  const user = getCurrentUser()
  if (!user) {
    await modal.error('Sie sind nicht authentifiziert. Bitte melden Sie sich an.', 'Authentifizierung erforderlich')
    router.push('/login')
    return
  }
  
  // Stelle sicher, dass wir ein frisches Token haben
  try {
    await user.getIdToken(true)
  } catch (error) {
    handleError(error, 'Authentifizierungsfehler')
    router.push('/login')
    return
  }
  
  loadProjects()
  loadImages()
  loadFavicon()
  loadShareImage()
  loadSubmissions()
  
  // Migration ausführen (für bestehende Projekte ohne Order)
  await migrateProjectOrders()
  await loadProjects()
})
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
